import * as React from 'react';
import { useRouter } from 'next/router';
import { Menu, Transition, Combobox } from '@headlessui/react';
import debounce from 'lodash/debounce';
import { AiOutlineStar, AiOutlineDotChart } from 'react-icons/ai';
import { GoGraph } from 'react-icons/go';
import { BiNews } from 'react-icons/bi';
import { AiOutlineAreaChart } from 'react-icons/ai';
import { IoLogoReact } from 'react-icons/io5';
import { RiLightbulbFlashLine, RiArrowUpDownFill, RiSignalTowerFill } from 'react-icons/ri';
import { CaretDown16 } from '@carbon/icons-react';
import { DatePicker, DatePickerInput } from 'carbon-components-react';
import { trackEvent } from 'services/client/analytics';
import {
  FLOW_METRIC_MODE_CHANGED,
  FLOW_TOGGLE_SIDE_MODULE,
  FLOW_EXPAND_PANE,
  FLOW_RESTORE_PANE,
} from 'services/client/analytics/events';
import classNames from 'styles/utils/classNames';
import MiniInput from 'components/MiniInput';
import PageTitle from 'components/PageTitle';
import MarketDataStatus from 'components/MarketDataStatus';
import useFlowAccess from 'hooks/useFlowAccess';
import Dropdown from 'components/Dropdown';
import { LayoutGrid, MainColumnDynamic, StackedColumn } from 'components/Layout';
import PutCallCard from 'components/SentimentCards/PutCallCard';
import CallPutMixCard from 'components/SentimentCards/CallPutMixCard';
import Card from 'components/Card';
import { CardRow } from 'components/SentimentCards/styles';
import OptionAggregatedTable from 'components/OptionAggregatedTable';
import CommandCenter from 'components/CommandCenter';
import Slider from 'components/Slider';
import Modal from 'components/Modal/HeadlessUiModal';
import { TickerAggregatedDetails, DarkPoolAggregate } from 'components/OptionAggregatedTable/props';
import DarkPoolTable from 'components/DarkPoolTable/DarkPoolTable';
import TopMoversTable from 'components/TopMoversTable';
import SignalAlertTable from 'components/SignalAlertTable';
import MostActiveTickers from 'components/MostActiveTickers';
import LayoutToggleButton from 'components/LayoutToggleButton';
import useSinglePaneLayout, { PaneId, getSinglePaneClass } from 'hooks/useSinglePaneLayout';
import getSignaturePrintStatus from 'utils/shared/trades/getSignaturePrintStatus';
import OptionsPopover from 'components/OptionFlowTable/OptionsPopover';
import { getIsGoldenDarkPool } from 'utils/shared/trades/getIsGoldenDarkPool';
import {
  useCommandCenter,
  getFlowFilterFunction,
  getTradeMatchesReadLabels,
  getDarkPoolFilterFunction,
  getAggregateFilterFunction,
} from 'hooks/useCommandCenter';
import { useEarnings } from 'hooks/useEarnings';
import {
  TICKER_FLOW_PAGE,
  OPTION_FLOW_PAGE,
  HISTORICAL_FLOW_PAGE,
  WEEKLY_OVERVIEW_PAGE,
  PageIcons,
} from 'constants/pages';
import calculatePercentOtm from 'utils/shared/trades/calculatePercentOtm';
import {
  PresetType,
  isEquityPreset,
  isUnusualPreset,
  isSweepPreset,
  isMomentumPreset,
  isEtfPreset,
  isMoonshotPreset,
  presetObjects,
  presetAggregateSortKey,
  presetGraphIndex,
  EQUITY_PRESET_FILTER,
  mergeFlowPresetState,
  normalizeFlowFilterState,
  normalizeFlowSideBasisSelection,
  getEffectiveFlowMetricMode,
  parseFlowMetricMode,
  type FlowMetricMode,
  type FlowSideBasis,
} from 'utils/shared/trades/presets';
import { mapCondensedSectors, ETF_SECTOR } from 'utils/shared/trades/sectors';
import { SortDirection, SignaturePrintStatus } from 'utils/shared/trades/types';
import getDerivedTradeValues from 'utils/shared/trades/getDerivedTradeValues';
import { adaptFlowRow } from 'utils/shared/trades/realtimeFlowAdapter';
import type { FlowRow } from 'constants/payloads/realtime';
import {
  useUpdateUserOptionFlowFiltersMutation,
  GetOptionFlowQuery,
  GetWeeklyOverviewQuery,
  useGetUserFlowConfigurationsQuery,
  useArchiveFlowConfigurationByPkMutation,
  useGetUserFlowConfigurationsLazyQuery,
} from 'types/generated/client';
import { hasDashboardAccess } from 'services/client/token';
import { AuthStatus } from 'constants/auth';
import {
  USE_TIMESTAMP_POLLING,
  getInitialPollingTimestamp,
  REALTIME_DARKPOOL_ENABLED,
} from 'constants/flags';
import { REALTIME_URL } from 'constants/api';
import { useDarkPoolRealtime } from 'components/DarkPoolTable/useDarkPoolRealtime';
import { realtimeFeedTypeCount } from './realtimeDarkpool';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useWatchlist } from 'hooks/useWatchlist';
import { searchTickerFilter } from 'utils/shared/tickers/searchTickerFilter';
import { buildTickerDetailsMap } from 'utils/shared/tickers/buildTickerDetailsMap';
import { getTickerList, TickerType } from 'services/client/getTickerList';
import {
  buildSectorOptionFlowAggregates,
  buildTickerOptionFlowAggregates,
  calculateDirectionalFlowValue,
} from 'utils/shared/trades/aggregateOptionFlow';
import { calculateCallPutMix } from 'utils/shared/trades/calculateCallPutMix';
import { getFlowSideBasisLabel } from 'utils/shared/trades/flowSideBasis';
import { sortOptionFlowTrades } from 'utils/shared/trades/sortOptionFlowTrades';
import {
  getTickerAggregateSortKeyForMetricMode,
  sortTickerAggregates,
} from 'utils/shared/trades/sortTickerAggregates';
import {
  useGetDarkPoolTradesFullQuery,
  useGetDarkPoolTradesCountFullQuery,
  useGetDarkPoolTradesCountFullFlowLazyQuery,
  useGetLitPoolTradesCountFullFlowLazyQuery,
  useGetAllPoolTradesCountFullFlowLazyQuery,
  Timezone_Location_Enum,
  useUpdateUserSoundSettingMutation,
  useHasNewDarkPoolTradesQuery,
} from 'types/generated/client';
import { FINRA_EXCHANGE_ID } from 'constants/darkpool';
import { MAX_FREE_TRADES } from 'constants/user';
import TopTickerGraphs, { DEFAULT_TOP_TICKER_GRAPH_INDEX } from './TopTickerGraphs';
import DarkPoolGraphs from './DarkPoolGraphs';
import SectorGraphs, { DEFAULT_SECTOR_GRAPH_INDEX } from './SectorGraphs';
import WatchList from './WatchList';
import TopNewsEvents from './TopNewsEvents';
import FlowDescription from './FlowDescription';
import { ListFilterModal } from './ListFilterModal';
import { SaveFilterModal } from './SaveFilterModal';
import WelcomeSlidePanel from './WelcomeSlidePanel';
import FlowAiAnalysisModal from './FlowAiAnalysisModal';
import FiltersButton from 'components/CommandCenter/FiltersButton';
import FlowSegmentedControl from 'components/FlowSegmentedControl';
import FlowTickerTypeSelector from 'components/FlowTickerTypeSelector';
import FlowSidesSelector from 'components/FlowSidesSelector';
import FlowPresetSelector from 'components/FlowPresetSelector';
import {
  Props,
  SectorAggregatedDetails,
  DarkPoolSectorAggregatedDetails,
  DarkPoolTickerAggregatedDetails,
  DarkPoolTrade,
  DEFAULT_FLOW_KEY,
  DEFAULT_AGGREGATE_KEY,
  FLOW_TABLE_HEADERS,
  LayoutType,
  FeedType,
} from './props';

import {
  Label,
  Button,
  FieldWrapper,
  ShowTripleColumns,
  LayoutButtons,
  HideLarge,
  ShowLarge,
  LayoutButtonRow,
  DarkFeedTypeToggle,
} from './styles';

type InjectedTrades =
  | GetOptionFlowQuery['option_flow']
  | GetWeeklyOverviewQuery['weekly_option_flow']
  | undefined;

const DARKPOOL_POLL_INTERVAL = 30_000;
const HEADER_ROW_COUNT = 1;
const TABLE_IDS = {
  PRIMARY: 'primary-table',
  SECONDARY: 'secondary-table',
} as const;
const AUDIO_NOTIFICATION_URL = '/audio/notification1.mp3';
const PAGE_DROPDOWN_ITEMS = [
  {
    name: (
      <span className="flex items-center">
        <PageIcons.OptionFlow /> <span className="ml-2">Realtime</span>
      </span>
    ),
    href: OPTION_FLOW_PAGE,
  },
  {
    name: (
      <span className="flex items-center">
        <PageIcons.HistoricalFlow /> <span className="ml-2">Historical</span>
      </span>
    ),
    href: HISTORICAL_FLOW_PAGE,
  },
  {
    name: (
      <span className="flex items-center">
        <PageIcons.WeeklyOverview /> <span className="ml-2">Weekly</span>
      </span>
    ),
    href: WEEKLY_OVERVIEW_PAGE,
  },
];
const SLIDER_TABS = ['Stacked', 'Market', 'Flow'];
// The named presets shown in the top nav. Ticker type (Equities/ETFs/All) and trade
// sides now live in their own dedicated controls, so the old Equities/ETFs/Custom tabs
// are gone — a preset only tunes the "unusual activity" criteria. `PRESET_TAB_TYPES` maps
// each slider position to its `PresetType` since the tab order no longer equals the enum.
const PRESET_TABS = ['Unusual', 'Sweeps', 'Momentum', 'Moonshots'];
const PRESET_TAB_TYPES = [
  PresetType.Unusual,
  PresetType.Sweeps,
  PresetType.Momentum,
  PresetType.Moonshots,
];
const FLOW_METRIC_MODE_ITEMS = [
  { value: 'total', label: 'Total' },
  { value: 'net', label: 'Net' },
];
enum RightColumnList {
  DarkPool = 'DARK_POOL',
  DarkPoolCharts = 'DARK_POOL_CHARTS',
  Graphs = 'GRAPHS',
  TickerGraphs = 'TICKER_GRAPHS',
  SectorGraphs = 'SECTOR_GRAPHS',
  WatchList = 'WATCH_LIST',
  TopNews = 'TOP_NEWS',
  TopMovers = 'TOP_MOVERS',
  Signals = 'SIGNALS',
  MostResearched = 'MOST_RESEARCHED',
}

const FLOW_EXPANDABLE_WIDGETS: ReadonlySet<RightColumnList> = new Set([
  RightColumnList.DarkPool,
  RightColumnList.Signals,
  RightColumnList.WatchList,
  RightColumnList.TopNews,
  RightColumnList.MostResearched,
]);

const isFlowExpandable = (key: RightColumnList) => FLOW_EXPANDABLE_WIDGETS.has(key);

// const RightColumnItem: React.FC<{ text: string; Icon: IconType }> = ({ text, Icon }) => {
//   return (
//     <span className="flex items-center">
//       <Icon />
//       <span className="ml-2">{text}</span>
//     </span>
//   );
// };

const rightColumnItems = [
  {
    key: RightColumnList.TickerGraphs,
    Icon: GoGraph,
    text: 'Options Top Tickers',
    name: (
      <span className="flex items-center">
        <GoGraph />
        <span className="ml-2">Options Top Tickers</span>
      </span>
    ),
  },
  {
    key: RightColumnList.SectorGraphs,
    Icon: AiOutlineDotChart,
    text: 'Options Sector Sentiment',
    name: (
      <span className="flex items-center">
        <AiOutlineDotChart />
        <span className="ml-2">Options Sector Sentiment</span>
      </span>
    ),
  },
  {
    key: RightColumnList.DarkPool,
    Icon: RiLightbulbFlashLine,
    text: 'Dark Pool Flow',
    name: (
      <span className="flex items-center">
        <RiLightbulbFlashLine />
        <span className="ml-2">Dark Pool Flow</span>
      </span>
    ),
  },
  {
    key: RightColumnList.DarkPoolCharts,
    Icon: AiOutlineAreaChart,
    text: 'Dark Pool Top Tickers',
    name: (
      <span className="flex items-center">
        <AiOutlineAreaChart />
        <span className="ml-2">Dark Pool Top Tickers</span>
      </span>
    ),
  },
  {
    key: RightColumnList.Signals,
    Icon: RiSignalTowerFill,
    text: 'Signals and Alerts',
    name: (
      <span className="flex items-center">
        <RiSignalTowerFill />
        <span className="ml-2">Signals and Alerts</span>
      </span>
    ),
  },
  {
    key: RightColumnList.WatchList,
    Icon: AiOutlineStar,
    text: 'Watch List',
    name: (
      <span className="flex items-center">
        <AiOutlineStar />
        <span className="ml-2">Watch List</span>
      </span>
    ),
  },
  {
    key: RightColumnList.TopNews,
    Icon: BiNews,
    text: 'Top News',
    name: (
      <span className="flex items-center">
        <BiNews />
        <span className="ml-2">Top News</span>
      </span>
    ),
  },
  {
    key: RightColumnList.TopMovers,
    Icon: RiArrowUpDownFill,
    text: 'Top Movers',
    name: (
      <span className="flex items-center">
        <RiArrowUpDownFill />
        <span className="ml-2">Top Movers</span>
      </span>
    ),
  },
  {
    key: RightColumnList.MostResearched,
    Icon: IoLogoReact,
    text: 'Most Researched Tickers',
    name: (
      <span className="flex items-center">
        <IoLogoReact />
        <span className="ml-2">Most Researched Tickers</span>
      </span>
    ),
  },
];
export const MAX_HISTORICAL_RESULTS = 10000;

const instanceOfOptionFlow = (object: any): object is GetOptionFlowQuery => {
  return !!object && !!object['option_flow'];
};

const instanceOfWeeklyOverview = (object: any): object is GetWeeklyOverviewQuery => {
  return !!object && !!object['weekly_option_flow'];
};

const getInjectedTrades = (
  injectedPageData?: GetOptionFlowQuery | GetWeeklyOverviewQuery,
): InjectedTrades => {
  if (!injectedPageData) {
    return undefined;
  }

  if (instanceOfOptionFlow(injectedPageData)) {
    return injectedPageData.option_flow;
  }

  if (instanceOfWeeklyOverview(injectedPageData)) {
    return injectedPageData.weekly_option_flow;
  }

  return undefined;
};

const getRowsForLayout = (layout: LayoutType) => {
  if (layout === LayoutType.Stacked) {
    return 2;
  }

  return 1;
};

// Reusable FeedTypeButtonGroup component
interface FeedTypeButtonGroupProps {
  feedType: FeedType;
  setFeedType: (type: FeedType) => void;
}

const FeedTypeButtonGroup: React.FC<FeedTypeButtonGroupProps> = ({ feedType, setFeedType }) => {
  const feedTypeButtons = [
    { type: FeedType.Dark, label: 'Dark' },
    { type: FeedType.Lit, label: 'Lit' },
    { type: FeedType.All, label: 'All' },
  ];

  return (
    <div className="flex flex-shrink-0 items-center">
      {feedTypeButtons.map((button) => (
        <DarkFeedTypeToggle
          key={button.type}
          isActive={feedType === button.type}
          onClick={() => setFeedType(button.type)}
        >
          {button.label}
        </DarkFeedTypeToggle>
      ))}
    </div>
  );
};

const OptionFlowPage: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const {
    isWeekly,
    shouldShowDatePicker,
    OptionFlowTable,
    title,
    Icon,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    tickers,
    setTickers,
    maxResults: _maxResults,
    setMaxResults: _setMaxResults,
    handleHistoricalSearch,
    onFlowSideBasisChange,
    viewer,
    flowSliderReplacement,
    defaultLayoutType,
    injectedPageData,
    pageHref,
    isShowFlowDescription,
    totalFlowCount,
    delayedFlowWait,
    darkPoolTrades: historicalDarkPoolTrades,
  } = props;
  /**
   * @todo use this
   */
  // const { get } = useApiGateway('/v1/products/option-flow');
  const additonalRows = shouldShowDatePicker ? 1 : 0;
  const isRealtimeFlow = !shouldShowDatePicker && !isWeekly;
  const flowAccess = useFlowAccess();
  // Mid / Bid / Below Bid sides are premium. Only lock once entitlement resolves to a
  // non-paid viewer ('delayed'); while 'unknown' (still loading) render no plan claim.
  const arePremiumSidesLocked = flowAccess === 'delayed';
  const router = useRouter();
  const { user: currentUser } = useCurrentUser();
  const { bookmarkedItems, handleBookmark, watchListCalled, watchListLoading } = useWatchlist();
  const { earningsMaps, fetchEarnings } = useEarnings();

  React.useEffect(() => {
    fetchEarnings();
  }, [fetchEarnings]);

  const timezoneLocation = currentUser?.timezoneLocation || Timezone_Location_Enum.Current;
  const isWallstreetTimezone = timezoneLocation === Timezone_Location_Enum.Wallstreet;
  const hasAccess = viewer.status === AuthStatus.User && hasDashboardAccess(viewer);
  const isUserSession = viewer.status === AuthStatus.User;

  const {
    data: fullData,
    loading: fullLoading,
    error: fullError,
    refetch: fullRefetch,
  } = useGetDarkPoolTradesFullQuery({
    skip: !hasAccess,
    fetchPolicy: 'no-cache',
  });

  const { data: fullCountData } = useGetDarkPoolTradesCountFullQuery({
    skip: !hasAccess || shouldShowDatePicker, // Skip count queries for historical data
    fetchPolicy: 'no-cache',
  });

  // New count queries for Dark/Lit/All feed types
  // These are only needed for real-time data that needs polling
  const [darkPoolCountQuery, { data: darkCountData, refetch: darkCountRefetch }] =
    useGetDarkPoolTradesCountFullFlowLazyQuery();
  const [litPoolCountQuery, { data: litCountData, refetch: litCountRefetch }] =
    useGetLitPoolTradesCountFullFlowLazyQuery();
  const [allPoolCountQuery, { data: allCountData, refetch: allCountRefetch }] =
    useGetAllPoolTradesCountFullFlowLazyQuery();

  const loading = fullLoading;
  const error = fullError;
  const refetch = fullRefetch;

  // Realtime (Phase 1b): the /flow dark-pool widgets ride the same darkpool DO
  // feed as the standalone table (components/DarkPoolTable). The wire rows ARE
  // the `equity_print_flow_full` view: that view is the complete current-day
  // `equity_print_feed_baseline` (hasura .../equity_print_flow_full/up.sql),
  // and the DO carries exactly that baseline. Rows are used UNFILTERED — do
  // NOT filter on `isFilteredFlow`: it's a wire-only stamp for the filtered
  // `equity_print_flow` surfaces, and DO-backfilled rows never carry it
  // (DarkPoolFeed's DARKPOOL_FIELDS doesn't select it), so filtering on it
  // discards whole backfill snapshots. The Dark/Lit tabs keep splitting by
  // exchangeId downstream. While live, every darkpool poll below (count
  // interval, timestamp poll, count-driven refetch) is suppressed; on
  // fallback/off the legacy polling re-engages automatically. Historical
  // date-picker mode and injected demo pages always use the legacy path.
  const realtimeDpEnabled =
    REALTIME_DARKPOOL_ENABLED &&
    !!REALTIME_URL &&
    hasAccess &&
    !shouldShowDatePicker &&
    !injectedPageData;
  const { rows: realtimeDpRows, status: realtimeDpStatus } = useDarkPoolRealtime(realtimeDpEnabled);
  // Sticky once live — same rule as the standalone container (§17.2 P1).
  const usingRealtimeDp =
    realtimeDpEnabled &&
    (realtimeDpStatus === 'live' ||
      (realtimeDpStatus === 'connecting' && realtimeDpRows.length > 0));
  const realtimeFlowFullRows = realtimeDpRows as unknown as DarkPoolTrade[];

  const realtimeDarkpoolTrades: DarkPoolTrade[] = usingRealtimeDp
    ? realtimeFlowFullRows
    : fullData?.equity_print_flow_full || [];

  const darkpoolTrades =
    shouldShowDatePicker && historicalDarkPoolTrades !== undefined
      ? historicalDarkPoolTrades
      : realtimeDarkpoolTrades;

  // Initialize with 24 hours ago to ensure initial data load
  const [lastSeenDarkPoolTimestamp, setLastSeenDarkPoolTimestamp] = React.useState<string>(
    getInitialPollingTimestamp,
  );

  // Timestamp-based polling
  const {
    data: darkPoolLatestData,
    startPolling: startDarkPoolPolling,
    stopPolling: stopDarkPoolPolling,
  } = useHasNewDarkPoolTradesQuery({
    variables: { since: lastSeenDarkPoolTimestamp },
    skip: !USE_TIMESTAMP_POLLING || !hasAccess,
  });
  const [updateFiltersMutation] = useUpdateUserOptionFlowFiltersMutation();
  const [updateUserSoundSetting] = useUpdateUserSoundSettingMutation();
  const { state: filterState, dispatch, numberOfFilters, ActionTypes } = useCommandCenter();
  const [searchValue, setSearchValue] = React.useState('');
  const [tickerData, setTickerData] = React.useState<TickerType[]>([]);
  const [tickerSearchResults, setTickerSearchResults] = React.useState<TickerType[]>([]);

  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 1025;
    }
    return false;
  });

  React.useEffect(() => {
    const handleResize = debounce(() => {
      setIsMobile(window.innerWidth < 1025);
    }, 150);

    window.addEventListener('resize', handleResize);
    return () => {
      handleResize.cancel();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const [activeRightColumnSectionTop, setActiveRightColumnSectionTop] = React.useState(
    RightColumnList.TickerGraphs,
  );
  const [activeRightColumnSectionBot, setActiveRightColumnSectionBot] = React.useState(
    RightColumnList.DarkPool,
  );
  const [activeRightColumnSectionLarge, setActiveRightColumnSectionLarge] = React.useState(
    RightColumnList.Signals,
  );
  const splitLayout = useSinglePaneLayout();
  const handleSelectTopWidget = React.useCallback(
    (key: RightColumnList) => {
      setActiveRightColumnSectionTop(key);
      trackEvent(FLOW_TOGGLE_SIDE_MODULE, { module: key, section: 'top' });
      if (splitLayout.expandedPane === 'top' && !isFlowExpandable(key)) {
        splitLayout.restore();
        trackEvent(FLOW_RESTORE_PANE, { pane: 'top', reason: 'ineligible_widget', module: key });
      }
    },
    [splitLayout],
  );
  const handleSelectBottomWidget = React.useCallback(
    (key: RightColumnList) => {
      setActiveRightColumnSectionBot(key);
      trackEvent(FLOW_TOGGLE_SIDE_MODULE, { module: key, section: 'bottom' });
      if (splitLayout.expandedPane === 'bottom' && !isFlowExpandable(key)) {
        splitLayout.restore();
        trackEvent(FLOW_RESTORE_PANE, { pane: 'bottom', reason: 'ineligible_widget', module: key });
      }
    },
    [splitLayout],
  );
  const handleToggleExpandPane = React.useCallback(
    (pane: PaneId, currentModule: RightColumnList) => {
      if (splitLayout.isExpanded(pane)) {
        splitLayout.restore();
        trackEvent(FLOW_RESTORE_PANE, { pane, reason: 'user_toggle', module: currentModule });
      } else {
        splitLayout.expand(pane);
        trackEvent(FLOW_EXPAND_PANE, { pane, module: currentModule });
      }
    },
    [splitLayout],
  );

  // Wide-screen (>=1760px) shows two stacked columns of two panes each. Defaults
  // mirror the previous fixed widgets so existing users see no change on load.
  const [activeWideTopLeft, setActiveWideTopLeft] = React.useState(RightColumnList.TickerGraphs);
  const [activeWideBotLeft, setActiveWideBotLeft] = React.useState(RightColumnList.SectorGraphs);
  const [activeWideBotRight, setActiveWideBotRight] = React.useState(RightColumnList.DarkPool);
  const splitLayoutWideLeft = useSinglePaneLayout();
  const splitLayoutWideRight = useSinglePaneLayout();

  const makeWideSelectHandler =
    (
      setter: React.Dispatch<React.SetStateAction<RightColumnList>>,
      layout: typeof splitLayoutWideLeft,
      pane: PaneId,
      section: string,
    ) =>
    (key: RightColumnList) => {
      setter(key);
      trackEvent(FLOW_TOGGLE_SIDE_MODULE, { module: key, section });
      if (layout.expandedPane === pane && !isFlowExpandable(key)) {
        layout.restore();
        trackEvent(FLOW_RESTORE_PANE, { pane, reason: 'ineligible_widget', module: key });
      }
    };

  const handleSelectWideTopLeft = React.useCallback(
    makeWideSelectHandler(setActiveWideTopLeft, splitLayoutWideLeft, 'top', 'wide_top_left'),
    [splitLayoutWideLeft],
  );
  const handleSelectWideBotLeft = React.useCallback(
    makeWideSelectHandler(setActiveWideBotLeft, splitLayoutWideLeft, 'bottom', 'wide_bottom_left'),
    [splitLayoutWideLeft],
  );
  const handleSelectWideTopRight = React.useCallback(
    makeWideSelectHandler(
      setActiveRightColumnSectionLarge,
      splitLayoutWideRight,
      'top',
      'wide_top_right',
    ),
    [splitLayoutWideRight],
  );
  const handleSelectWideBotRight = React.useCallback(
    makeWideSelectHandler(
      setActiveWideBotRight,
      splitLayoutWideRight,
      'bottom',
      'wide_bottom_right',
    ),
    [splitLayoutWideRight],
  );

  const makeWideToggleHandler =
    (layout: typeof splitLayoutWideLeft) => (pane: PaneId, currentModule: RightColumnList) => {
      if (layout.isExpanded(pane)) {
        layout.restore();
        trackEvent(FLOW_RESTORE_PANE, { pane, reason: 'user_toggle', module: currentModule });
      } else {
        layout.expand(pane);
        trackEvent(FLOW_EXPAND_PANE, { pane, module: currentModule });
      }
    };
  const handleToggleWideLeftPane = React.useCallback(makeWideToggleHandler(splitLayoutWideLeft), [
    splitLayoutWideLeft,
  ]);
  const handleToggleWideRightPane = React.useCallback(makeWideToggleHandler(splitLayoutWideRight), [
    splitLayoutWideRight,
  ]);

  const [audio, setAudio] = React.useState<HTMLAudioElement | null>(null);
  const [isFlowWatchListOnly, setIsFlowWatchListOnly] = React.useState(false);
  const [isAggregateWatchListOnly, setIsAggregateWatchListOnly] = React.useState(false);
  const [isCommandCenterOpen, setIsCommandCenterOpen] = React.useState(false);
  const [topTickerGraph, setTopTickerGraph] = React.useState(DEFAULT_TOP_TICKER_GRAPH_INDEX);
  const [activeSectorGraph, setActiveSectorGraph] = React.useState(DEFAULT_SECTOR_GRAPH_INDEX);
  const [layoutIndex, setLayoutIndex] = React.useState(defaultLayoutType || LayoutType.Stacked);
  const [mobileLayoutIndex, setMobileLayoutIndex] = React.useState<LayoutType | string>(
    defaultLayoutType !== undefined ? defaultLayoutType : LayoutType.Stacked,
  );

  // React.useEffect(() => {
  //   if (isMobile && mobileLayoutIndex === LayoutType.Stacked) {
  //     setMobileLayoutIndex(LayoutType.Flow);
  //   }
  // }, [isMobile, mobileLayoutIndex]);

  const [isSaveModalOpen, setIsSaveModalOpen] = React.useState(false);
  const [isListFilterModalOpen, setIsListFilterModalOpen] = React.useState(false);
  // `Custom` (= not one of the named presets) renders no highlighted tab; hydration and
  // preset clicks promote this to a real preset when the criteria match.
  const [presetIndex, setPresetIndex] = React.useState<PresetType>(PresetType.Custom);
  const [aggregateSortKey, setAggregateSortKey] = React.useState(DEFAULT_AGGREGATE_KEY);
  const [aggregateSortDirection, setAggregateSortDirection] = React.useState(SortDirection.None);
  const [flowSortKey, setFlowSortKey] = React.useState(DEFAULT_FLOW_KEY);
  const [flowSortDirection, setFlowSortDirection] = React.useState(SortDirection.None);
  const [isFlowSoundEnabled, setIsFlowSoundEnabled] = React.useState(
    currentUser?.isSoundEnabled ?? false,
  );
  const [activeFilter, setActiveFilter] = React.useState('');
  const [visibleFeedTypes, setVisibleFeedTypes] = React.useState<Record<string, FeedType>>({
    [TABLE_IDS.PRIMARY]: FeedType.Dark,
    [TABLE_IDS.SECONDARY]: FeedType.Dark,
  });
  const [isWatchListModalOpen, setIsWatchListModalOpen] = React.useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [selectedPopoverTrade, setSelectedPopoverTrade] = React.useState<any>(null);
  const popoverAnchorRef = React.useRef<HTMLElement | null>(null);
  const hasHydratedSavedFlowFiltersRef = React.useRef(false);
  const flowSideLabel = getFlowSideBasisLabel(filterState.flowSideBasis);
  const selectedFlowMetricMode = parseFlowMetricMode(filterState.flowMetricMode) || 'total';
  const flowMetricMode = getEffectiveFlowMetricMode(selectedFlowMetricMode);

  React.useEffect(() => {
    onFlowSideBasisChange?.(filterState.flowSideBasis);
  }, [filterState.flowSideBasis, onFlowSideBasisChange]);

  const updateFeedType = React.useCallback((tableId: string, feedType: FeedType) => {
    setVisibleFeedTypes((prev) => ({ ...prev, [tableId]: feedType }));
  }, []);

  const uniqueFeedTypes = React.useMemo(
    () => [...new Set(Object.values(visibleFeedTypes))],
    [visibleFeedTypes],
  );

  React.useEffect(() => {
    (async () => {
      try {
        const data = await getTickerList();
        setTickerData(data);
      } catch (error) {
        setTickerData([]);
      }
    })();
  }, []);

  const searchTickers = React.useCallback(
    (query: string) => {
      if (!query || tickerData.length === 0) {
        setTickerSearchResults([]);
        return;
      }

      const parts = query.split(',');
      const lastPart = parts[parts.length - 1].trim();

      if (lastPart && lastPart.length >= 1) {
        const results = searchTickerFilter(lastPart, tickerData);
        setTickerSearchResults(results);
      } else {
        setTickerSearchResults([]);
      }
    },
    [tickerData],
  );

  const debouncedSearchTickers = React.useMemo(() => debounce(searchTickers, 300), [searchTickers]);

  // Cancel pending debounced search on unmount to prevent memory leaks
  React.useEffect(() => {
    return () => {
      debouncedSearchTickers.cancel();
    };
  }, [debouncedSearchTickers]);

  const handleTickerSelection = (ticker: TickerType | null) => {
    if (!ticker || !setTickers) return;

    const currentValue = tickers || '';
    const parts = currentValue
      .split(',')
      .map((t) => t.trim())
      .filter((t) => !!t);

    const hasIncompleteLastPart = currentValue.endsWith(',') === false && parts.length > 0;
    const completeTickers = hasIncompleteLastPart ? parts.slice(0, -1) : parts;

    if (!completeTickers.includes(ticker.t)) {
      const newTickers = [...completeTickers, ticker.t].join(',') + ',';
      setTickers(newTickers);
    } else {
      setTickers(completeTickers.join(',') + ',');
    }

    setTickerSearchResults([]);
  };

  const handleTickerInputChange = (value: string) => {
    if (!setTickers) return;

    const uppercaseValue = value.toUpperCase();
    setTickers(uppercaseValue);
    debouncedSearchTickers(uppercaseValue);
  };

  const applyCurrentFilters = React.useCallback(
    (newState: typeof filterState) => {
      const normalizedState = normalizeFlowFilterState(newState);

      dispatch({ type: ActionTypes.Set, payload: { items: normalizedState } });
    },
    [ActionTypes.Set, dispatch],
  );

  const resetCurrentFilters = React.useCallback(() => {
    dispatch({ type: ActionTypes.Reset });
  }, [ActionTypes.Reset, dispatch]);

  const handleFlowMetricModeChange = React.useCallback(
    (nextFlowMetricMode: FlowMetricMode) => {
      if (nextFlowMetricMode !== selectedFlowMetricMode) {
        trackEvent(FLOW_METRIC_MODE_CHANGED, {
          surface: 'dashboard_tables',
          previousMetricMode: selectedFlowMetricMode,
          nextMetricMode: nextFlowMetricMode,
          flowSideBasis: normalizeFlowSideBasisSelection(filterState.flowSideBasis).join(','),
        });
      }

      const newState = normalizeFlowFilterState(filterState, {
        flowMetricMode: nextFlowMetricMode,
      });

      if (aggregateSortDirection !== SortDirection.None) {
        const nextAggregateSortKey = getTickerAggregateSortKeyForMetricMode(
          aggregateSortKey,
          nextFlowMetricMode,
        );

        if (nextAggregateSortKey !== aggregateSortKey) {
          setAggregateSortKey(nextAggregateSortKey);
        }
      }

      dispatch({ type: ActionTypes.Set, payload: { items: newState } });
      if (viewer?.viewer?.uid) {
        updateFiltersMutation({
          variables: {
            id: viewer.viewer.uid,
            optionFlowFilters: newState,
          },
        });
      }
    },
    [
      ActionTypes.Set,
      aggregateSortDirection,
      aggregateSortKey,
      dispatch,
      filterState,
      selectedFlowMetricMode,
      updateFiltersMutation,
      viewer?.viewer?.uid,
    ],
  );

  // Ticker type (Equities/ETFs/All) is an independent, user-owned dimension — changing it
  // never touches presets or sides. Persist it like the other top-nav filter controls.
  const handleTickerTypeChange = React.useCallback(
    (nextTickerType: string) => {
      if (nextTickerType === filterState.tt) {
        return;
      }

      const newState = normalizeFlowFilterState({ ...filterState, tt: nextTickerType });
      dispatch({ type: ActionTypes.Set, payload: { items: newState } });

      if (viewer?.viewer?.uid) {
        updateFiltersMutation({
          variables: { id: viewer.viewer.uid, optionFlowFilters: newState },
        });
      }
    },
    [ActionTypes.Set, dispatch, filterState, updateFiltersMutation, viewer?.viewer?.uid],
  );

  // Trade sides (AA/A/M/B/BB) are likewise independent of presets — the multi-select math
  // lives in `toggleFlowSideSelection`; here we just apply and persist the resulting basis.
  const handleFlowSidesChange = React.useCallback(
    (nextSides: FlowSideBasis[]) => {
      const newState = normalizeFlowFilterState(filterState, { flowSideBasis: nextSides });
      dispatch({ type: ActionTypes.Set, payload: { items: newState } });

      if (viewer?.viewer?.uid) {
        updateFiltersMutation({
          variables: { id: viewer.viewer.uid, optionFlowFilters: newState },
        });
      }
    },
    [ActionTypes.Set, dispatch, filterState, updateFiltersMutation, viewer?.viewer?.uid],
  );

  // Apply a named preset: reshapes the activity criteria (and its companion sort + graph)
  // while mergeFlowPresetState preserves the user's ticker type and sides.
  const handleSelectPreset = React.useCallback(
    (presetType: PresetType) => {
      const presetObject = presetObjects[presetType];
      const presetAggregateSort = presetAggregateSortKey[presetType];
      const presetGraph = presetGraphIndex[presetType];
      setPresetIndex(presetType);

      if (presetObject) {
        const nextPresetState = mergeFlowPresetState(filterState, presetObject);
        dispatch({ type: ActionTypes.Set, payload: { items: nextPresetState } });

        if (viewer?.viewer?.uid) {
          updateFiltersMutation({
            variables: { id: viewer.viewer.uid, optionFlowFilters: nextPresetState },
          });
        }
      }

      if (presetAggregateSort) {
        setAggregateSortKey(presetAggregateSort);
        setAggregateSortDirection(SortDirection.Desc);
      }

      if (presetGraph || presetGraph === 0) {
        setTopTickerGraph(presetGraph);
      }
    },
    [ActionTypes.Set, dispatch, filterState, updateFiltersMutation, viewer?.viewer?.uid],
  );

  // Deselect the active preset: reset activity criteria back to default (preserving ticker
  // type + sides) and highlight no tab.
  const handleClearPreset = React.useCallback(() => {
    setPresetIndex(PresetType.Custom);
    const nextState = mergeFlowPresetState(filterState, EQUITY_PRESET_FILTER);
    dispatch({ type: ActionTypes.Set, payload: { items: nextState } });

    if (viewer?.viewer?.uid) {
      updateFiltersMutation({
        variables: { id: viewer.viewer.uid, optionFlowFilters: nextState },
      });
    }
  }, [ActionTypes.Set, dispatch, filterState, updateFiltersMutation, viewer?.viewer?.uid]);

  const injectedTrades: InjectedTrades = getInjectedTrades(injectedPageData);
  const initialTrades = injectedTrades || props.trades || [];
  // Derived from the provider's canonical set so the star state, the watchlist-only filters and
  // `handleBookmark` all agree on the same normalized tickers, including optimistic toggles.
  const watchListTickers = React.useMemo(() => Array.from(bookmarkedItems), [bookmarkedItems]);

  const unfilteredCount = fullCountData?.equity_print_flow_full_aggregate?.aggregate?.count;
  const count = shouldShowDatePicker
    ? darkpoolTrades.length
    : usingRealtimeDp
    ? realtimeFlowFullRows.length
    : unfilteredCount;

  const [countPrimary, countSecondary] = React.useMemo(() => {
    const getCountForFeedType = (feedType: FeedType) => {
      if (shouldShowDatePicker) {
        return darkpoolTrades.length;
      }

      const isDarkFeed = feedType === FeedType.Dark;
      const isLitFeed = feedType === FeedType.Lit;

      // Realtime: counts derive from the streamed rows — no count queries needed.
      if (usingRealtimeDp) {
        return realtimeFeedTypeCount(realtimeFlowFullRows, feedType);
      }

      return isDarkFeed
        ? darkCountData?.equity_print_flow_full_aggregate?.aggregate?.count
        : isLitFeed
        ? litCountData?.equity_print_flow_full_aggregate?.aggregate?.count
        : allCountData?.equity_print_flow_full_aggregate?.aggregate?.count;
    };

    return [
      getCountForFeedType(visibleFeedTypes[TABLE_IDS.PRIMARY]),
      getCountForFeedType(visibleFeedTypes[TABLE_IDS.SECONDARY]),
    ];
  }, [
    shouldShowDatePicker,
    darkpoolTrades.length,
    usingRealtimeDp,
    realtimeFlowFullRows,
    darkCountData,
    litCountData,
    allCountData,
    visibleFeedTypes,
  ]);

  // Skip for historical data - we don't need counts since it's a static snapshot
  React.useEffect(() => {
    if (!hasAccess || shouldShowDatePicker || usingRealtimeDp) return;

    uniqueFeedTypes.forEach((feedType) => {
      const isDarkFeed = feedType === FeedType.Dark;
      const isLitFeed = feedType === FeedType.Lit;

      const refetchFn = isDarkFeed
        ? darkCountRefetch
        : isLitFeed
        ? litCountRefetch
        : allCountRefetch;

      const queryFn = isDarkFeed
        ? darkPoolCountQuery
        : isLitFeed
        ? litPoolCountQuery
        : allPoolCountQuery;

      if (refetchFn) {
        refetchFn();
      } else {
        queryFn();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, uniqueFeedTypes, shouldShowDatePicker, usingRealtimeDp]);

  // Realtime feed sends ticker-level fields once per ticker (not inlined per row). When
  // present, join them here — in the SAME pass that derives display values — so we never
  // materialize a second full adapted array of every trade (memory: 3 resident copies → 2).
  // Legacy/injected/other surfaces pass no tickerInfo and rows already carry the fields, so
  // this is a pass-through. The two maps are also fused into one to drop the transient
  // intermediate array that used to be rebuilt every 500ms flush. Injected rows are never
  // realtime FlowRows, so they never take the join, even if a caller passes tickerInfo.
  const realtimeTickerInfo = injectedTrades ? undefined : props.tickerInfo;
  const trades = React.useMemo(
    () =>
      initialTrades.map((rawRow, i) => {
        const t = realtimeTickerInfo
          ? (adaptFlowRow(
              rawRow as unknown as FlowRow,
              realtimeTickerInfo,
            ) as unknown as typeof rawRow)
          : rawRow;
        const spot = t.spot || t.priceExternalApi;
        const enriched = {
          ...t,
          sector: t.tickerDetails?.sector || '',
          spot,
          otmPercent: calculatePercentOtm(t.strike, spot, t.cp || ''),
          isBlur: !injectedPageData && !hasAccess && i >= MAX_FREE_TRADES,
        };
        return getDerivedTradeValues(enriched, isWallstreetTimezone);
      }),
    [initialTrades, isWallstreetTimezone, hasAccess, realtimeTickerInfo],
  );

  // Previewed-side rows for the command center "live count". Derived the same way
  // as `trades` but kept entirely separate from the committed feed.
  const previewOptionFlowTrades = React.useMemo(() => {
    if (!props.previewTrades) return undefined;

    return props.previewTrades
      .map((t, i) => {
        const spot = t.spot || t.priceExternalApi;
        return {
          ...t,
          sector: t.tickerDetails?.sector || '',
          spot,
          otmPercent: calculatePercentOtm(t.strike, spot, t.cp || ''),
          isBlur: !injectedPageData && !hasAccess && i >= MAX_FREE_TRADES,
        };
      })
      .map((trade) => getDerivedTradeValues(trade, isWallstreetTimezone));
  }, [props.previewTrades, isWallstreetTimezone, hasAccess, injectedPageData]);
  const hasWatchListItems = watchListCalled && !watchListLoading && bookmarkedItems.size > 0;

  const handleCloseWatchListModal = () => {
    setIsWatchListModalOpen(false);
  };

  const handleOpenPopover = React.useCallback((trade: any, anchorElement?: HTMLElement) => {
    if (anchorElement) {
      popoverAnchorRef.current = anchorElement;
    }
    setSelectedPopoverTrade(trade);

    setTimeout(() => {
      setIsPopoverOpen(true);
    }, 0);
  }, []);

  React.useEffect(() => {
    const watchListHasLoaded = watchListCalled && !watchListLoading;
    const toggledWatchListOn = isFlowWatchListOnly || isAggregateWatchListOnly;
    if (toggledWatchListOn && watchListHasLoaded && !hasWatchListItems) {
      setActiveRightColumnSectionTop(RightColumnList.WatchList);
    }
  }, [
    isFlowWatchListOnly,
    isAggregateWatchListOnly,
    hasWatchListItems,
    watchListCalled,
    watchListLoading,
  ]);

  React.useEffect(() => {
    if (!hasAccess || shouldShowDatePicker || usingRealtimeDp) return;
    if (USE_TIMESTAMP_POLLING) return; // Skip interval polling when timestamp polling is on

    let interval: ReturnType<typeof setInterval> | undefined;

    const refetchActiveCountQueries = () => {
      uniqueFeedTypes.forEach((feedType) => {
        const isDarkFeed = feedType === FeedType.Dark;
        const isLitFeed = feedType === FeedType.Lit;

        const refetchFn = isDarkFeed
          ? darkCountRefetch
          : isLitFeed
          ? litCountRefetch
          : allCountRefetch;

        if (refetchFn) {
          refetchFn();
        }
      });
    };

    refetchActiveCountQueries();

    // @ts-ignore issue with Node / browser types
    interval = setInterval(() => {
      refetchActiveCountQueries();
    }, DARKPOOL_POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [
    hasAccess,
    shouldShowDatePicker,
    usingRealtimeDp,
    uniqueFeedTypes,
    darkCountRefetch,
    litCountRefetch,
    allCountRefetch,
  ]);

  React.useEffect(() => {
    if (USE_TIMESTAMP_POLLING || usingRealtimeDp) return;

    if (hasAccess) {
      if (refetch) {
        refetch();
      }
    }
  }, [hasAccess, refetch, count, countPrimary, countSecondary, usingRealtimeDp]);

  // Control timestamp-based polling (feature flagged). Realtime supersedes it —
  // stop while the socket is live, restart automatically on fallback.
  React.useEffect(() => {
    if (!USE_TIMESTAMP_POLLING) return;

    if (hasAccess && !usingRealtimeDp) {
      startDarkPoolPolling(DARKPOOL_POLL_INTERVAL); // Always 30s, matching original behavior (no market hours check)
    } else {
      stopDarkPoolPolling();
    }

    return () => stopDarkPoolPolling();
  }, [hasAccess, usingRealtimeDp, startDarkPoolPolling, stopDarkPoolPolling]);

  // New timestamp-based refetch (feature flagged)
  React.useEffect(() => {
    if (!USE_TIMESTAMP_POLLING || usingRealtimeDp) return;

    if (hasAccess && darkPoolLatestData?.equity_print_flow?.[0]?.createdAt) {
      const newTimestamp = darkPoolLatestData.equity_print_flow[0].createdAt;
      if (newTimestamp !== lastSeenDarkPoolTimestamp) {
        setLastSeenDarkPoolTimestamp(newTimestamp);
        if (refetch) {
          refetch();
        }
      }
    }
  }, [hasAccess, darkPoolLatestData, lastSeenDarkPoolTimestamp, refetch, usingRealtimeDp]);

  React.useEffect(() => {
    setAudio(new Audio(AUDIO_NOTIFICATION_URL));
  }, []);

  React.useEffect(() => {
    if (router.isReady) {
      // Currently allow default to take precedence
      if (defaultLayoutType) {
        setLayoutIndex(defaultLayoutType);
      } else if (router.query.layout) {
        const layout = parseInt(router.query.layout as string, 10);
        setLayoutIndex(layout as LayoutType);
      }

      if (router.query.presetIndex) {
        const presetIndex = router.query.presetIndex as string;
        const presetIndexNumber = parseInt(presetIndex, 10);
        const presetObject = presetObjects[presetIndexNumber as PresetType];
        setPresetIndex(presetIndexNumber);

        if (presetObject) {
          dispatch({
            type: ActionTypes.Set,
            payload: {
              items: mergeFlowPresetState(filterState, presetObject),
            },
          });
        }
      }

      if (router.query.flowSortKey) {
        const overrideFlowSortKey = router.query.flowSortKey as string;
        setFlowSortKey(overrideFlowSortKey);
      }

      if (router.query.flowSortDirection) {
        const overrideFlowSortDirection = router.query.flowSortDirection as string;
        setFlowSortDirection(overrideFlowSortDirection as SortDirection);
      }

      if (router.query.aggregateSortKey) {
        const overrideAggregateSortKey = router.query.aggregateSortKey as string;
        setAggregateSortKey(overrideAggregateSortKey);
      }

      if (router.query.aggregateSortDirection) {
        const overrideAggregateSortDirection = router.query.aggregateSortDirection as string;
        setAggregateSortDirection(overrideAggregateSortDirection as SortDirection);
      }

      if (router.query.topTickerGraph) {
        const overrideTopTickerGraph = router.query.topTickerGraph as string;
        setTopTickerGraph(parseInt(overrideTopTickerGraph, 10));
      }

      if (router.query.activeSectorGraph) {
        const overrideActiveSectorGraph = router.query.activeSectorGraph as string;
        setActiveSectorGraph(parseInt(overrideActiveSectorGraph, 10));
      }
    }
  }, [router.isReady, defaultLayoutType]);

  const dailyVolume = injectedPageData?.daily_volume || props.dailyVolume || [];
  const dailyVolumeMap: Record<string, number> = React.useMemo(
    () =>
      dailyVolume.reduce((acc, curr) => {
        if (curr.ticker && curr.volume) {
          // @ts-ignore
          acc[curr.ticker] = curr.volume;
        }
        return acc;
      }, {} as Record<string, number>),
    [dailyVolume],
  );

  React.useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (router.query.presetIndex) {
      return;
    }

    if (props.userDetails?.optionFlowFilters && !hasHydratedSavedFlowFiltersRef.current) {
      hasHydratedSavedFlowFiltersRef.current = true;
      const hydratedOptionFlowFilters = normalizeFlowFilterState(
        props.userDetails.optionFlowFilters,
      );

      dispatch({
        type: ActionTypes.Set,
        payload: { items: hydratedOptionFlowFilters },
      });

      let presetIndex = PresetType.Custom;

      if (isEquityPreset(hydratedOptionFlowFilters)) {
        presetIndex = PresetType.Equities;
      }

      if (isUnusualPreset(hydratedOptionFlowFilters)) {
        presetIndex = PresetType.Unusual;
      }

      if (isSweepPreset(hydratedOptionFlowFilters)) {
        presetIndex = PresetType.Sweeps;
      }

      if (isMomentumPreset(hydratedOptionFlowFilters)) {
        presetIndex = PresetType.Momentum;
      }

      if (isEtfPreset(hydratedOptionFlowFilters)) {
        presetIndex = PresetType.ETFs;
      }

      if (isMoonshotPreset(hydratedOptionFlowFilters)) {
        presetIndex = PresetType.Moonshots;
      }

      const presetAggregateSort = presetAggregateSortKey[presetIndex];
      const presetGraph = presetGraphIndex[presetIndex];

      setPresetIndex(presetIndex);

      if (presetAggregateSort) {
        setAggregateSortKey(presetAggregateSort);
        setAggregateSortDirection(SortDirection.Desc);
      }

      if (presetGraph || presetGraph === 0) {
        setTopTickerGraph(presetGraph);
      }
    }
  }, [router.isReady, props.userDetails?.optionFlowFilters]);

  // On mobile, only compute sector data when viewing sector graphs
  const needsSectorData = !isMobile || mobileLayoutIndex === RightColumnList.SectorGraphs;

  const tradesBySector = React.useMemo(() => {
    if (!needsSectorData) return [];

    return buildSectorOptionFlowAggregates(trades, {
      flowSideBasis: filterState.flowSideBasis,
      putFlowBasis: filterState.putFlowBasis,
      shouldIncludeSector: (sector) =>
        sector !== 'ETF' || filterState.tt === 'ETFs' || filterState.tt !== 'Stocks',
    }) as SectorAggregatedDetails[];
  }, [
    trades,
    filterState.flowSideBasis,
    filterState.putFlowBasis,
    filterState.tt,
    needsSectorData,
  ]);

  const tickerDetailsMap = React.useMemo(() => buildTickerDetailsMap(tickerData), [tickerData]);

  // On mobile, only compute dark pool sector data when viewing dark pool charts
  const needsDarkPoolSectorData = !isMobile || mobileLayoutIndex === RightColumnList.DarkPoolCharts;

  const filteredDarkpoolBase = React.useMemo(() => {
    const fn = getDarkPoolFilterFunction(filterState);
    return darkpoolTrades.filter(fn);
  }, [darkpoolTrades, filterState]);

  const darkPoolBySector = React.useMemo(() => {
    if (!needsDarkPoolSectorData) return [];

    const groupedTrades: Record<string, DarkPoolSectorAggregatedDetails> = {};

    filteredDarkpoolBase.forEach((t) => {
      const originalSector = t.tickerDetails?.sector || tickerDetailsMap[t.ticker || '']?.sector;

      if (!!originalSector || t.isEtf) {
        const sector = t.isEtf
          ? ETF_SECTOR
          : originalSector
          ? mapCondensedSectors(originalSector)
          : '';
        if (!groupedTrades[sector]) {
          groupedTrades[sector] = {
            sector: sector,
            premium: 0,
            volume: 0,
            lateBuyCount: 0,
            lateSellCount: 0,
            goldenTradeCount: 0,
            avgVolume: t.avgVolume,
            dayVolume: t.dayVolume,
            marketCap: t.marketCap,
            isEtf: t.isEtf || sector === ETF_SECTOR,
          };
        }

        const signaturePrintStatus = getSignaturePrintStatus(t, { open: t.open });
        groupedTrades[sector].premium += t.amount || 0;
        groupedTrades[sector].volume += t.size || 0;

        if (signaturePrintStatus === SignaturePrintStatus.Buy) {
          groupedTrades[sector].lateBuyCount += 1;
        } else if (signaturePrintStatus === SignaturePrintStatus.Sell) {
          groupedTrades[sector].lateSellCount += 1;
        }

        if (getIsGoldenDarkPool(t.size, t.avgVolume)) {
          groupedTrades[sector].goldenTradeCount += 1;
        }
      }
    });

    return Object.values(groupedTrades);
  }, [filteredDarkpoolBase, tickerDetailsMap, needsDarkPoolSectorData]);

  const darkPoolByTicker = React.useMemo(() => {
    const groupedTrades: Record<string, DarkPoolTickerAggregatedDetails> = {};

    filteredDarkpoolBase.forEach((t) => {
      const ticker = t.ticker || '';
      const mapEntry = tickerDetailsMap[ticker];
      const originalSector = t.tickerDetails?.sector || mapEntry?.sector;
      const sector = t.isEtf
        ? ETF_SECTOR
        : originalSector
        ? mapCondensedSectors(originalSector)
        : '';
      if (!groupedTrades[ticker]) {
        groupedTrades[ticker] = {
          ticker,
          tickerName: t.tickerDetails?.name || mapEntry?.name || '',
          sector: sector,
          premium: 0,
          volume: 0,
          lateBuyCount: 0,
          lateSellCount: 0,
          goldenTradeCount: 0,
          avgVolume: t.avgVolume,
          dayVolume: t.dayVolume,
          marketCap: t.marketCap,
          momentum: 0,
          isEtf: t.isEtf || sector === ETF_SECTOR,
        };
      }

      const signaturePrintStatus = getSignaturePrintStatus(t, { open: t.open });
      groupedTrades[ticker].premium += t.amount || 0;
      groupedTrades[ticker].volume += t.size || 0;

      if (signaturePrintStatus === SignaturePrintStatus.Buy) {
        groupedTrades[ticker].lateBuyCount += 1;
      } else if (signaturePrintStatus === SignaturePrintStatus.Sell) {
        groupedTrades[ticker].lateSellCount += 1;
      }

      if (getIsGoldenDarkPool(t.size, t.avgVolume)) {
        groupedTrades[ticker].goldenTradeCount += 1;
      }
    });

    // Compute momentum after all trades are aggregated
    Object.keys(groupedTrades).forEach((ticker) => {
      groupedTrades[ticker].momentum =
        groupedTrades[ticker].volume /
        ((groupedTrades[ticker].avgVolume || groupedTrades[ticker].volume) / 2);
    });

    const result = Object.values(groupedTrades);
    return result as DarkPoolAggregate[];
  }, [filteredDarkpoolBase, tickerDetailsMap]);

  const filteredDarkPoolByTicker = React.useMemo(() => {
    const searchTerm = searchValue.trim().toUpperCase();
    if (!searchTerm) return darkPoolByTicker;
    return darkPoolByTicker.filter((trade) => trade.ticker?.toUpperCase().includes(searchTerm));
  }, [darkPoolByTicker, searchValue]);

  const readFilteredTradesForAggregates = React.useMemo(() => {
    return trades.filter((trade) => getTradeMatchesReadLabels(trade, filterState.readLabels));
  }, [trades, filterState.readLabels]);

  const tradesByTickerMap = React.useMemo(() => {
    return buildTickerOptionFlowAggregates(readFilteredTradesForAggregates, dailyVolumeMap, {
      flowSideBasis: filterState.flowSideBasis,
      putFlowBasis: filterState.putFlowBasis,
    }) as Record<string, TickerAggregatedDetails>;
  }, [
    readFilteredTradesForAggregates,
    dailyVolumeMap,
    filterState.flowSideBasis,
    filterState.putFlowBasis,
  ]);

  const tradesByTicker = React.useMemo(() => {
    const searchTerm = searchValue.trim();
    const aggregateFilterFunction = getAggregateFilterFunction(filterState);
    const values = Object.values(tradesByTickerMap).filter((trade) => {
      if (searchTerm && !trade.ticker?.includes(searchTerm)) {
        return false;
      }

      if (isAggregateWatchListOnly && (!trade.ticker || !bookmarkedItems.has(trade.ticker))) {
        return false;
      }

      return aggregateFilterFunction(trade);
    });
    return sortTickerAggregates({
      tickers: values,
      sortKey: aggregateSortKey,
      sortDirection: aggregateSortDirection,
      metricMode: flowMetricMode,
      defaultSortKey: DEFAULT_AGGREGATE_KEY,
    });
  }, [
    tradesByTickerMap,
    aggregateSortKey,
    aggregateSortDirection,
    searchValue,
    bookmarkedItems,
    isAggregateWatchListOnly,
    filterState,
    flowMetricMode,
  ]);

  const filteredTrades = React.useMemo(() => {
    const flowFilterFunction = getFlowFilterFunction(filterState, tradesByTickerMap);
    const filteredTrades = trades.filter(flowFilterFunction);

    if (isFlowWatchListOnly) {
      return filteredTrades.filter((trade) => {
        return !!trade.ticker && bookmarkedItems.has(trade.ticker);
      });
    }

    return filteredTrades;
  }, [trades, filterState, tradesByTickerMap, bookmarkedItems, isFlowWatchListOnly]);

  const searchedTrades = React.useMemo(() => {
    const searchedTicker = searchValue.trim();
    if (!searchedTicker) {
      return filteredTrades;
    } else {
      return filteredTrades.filter((t) => t.ticker?.includes(searchedTicker));
    }
  }, [filteredTrades, searchValue]);

  const displayedTrades = React.useMemo(() => {
    return sortOptionFlowTrades({
      trades: searchedTrades,
      headers: FLOW_TABLE_HEADERS,
      sortKey: flowSortKey,
      sortDirection: flowSortDirection,
      defaultSortKey: DEFAULT_FLOW_KEY,
    });
  }, [searchedTrades, flowSortKey, flowSortDirection]);

  const {
    putContracts,
    callContracts,
    putPremium,
    callPremium,
    bullishPremium,
    bearishPremium,
    neutralPremium,
  } = React.useMemo(() => {
    // `searchedTrades`, not `displayedTrades`: sorting cannot change an aggregate, and
    // keying on the sorted array re-walked up to 50k trades on every sort-header click.
    //
    // No side-basis filter here either. `getFlowFilterFunction` already drops anything
    // that fails `getTradeMatchesFlowSideBasis` (hooks/useCommandCenter.ts), so these
    // trades are side-filtered before they arrive — the second pass was a provable no-op
    // allocating a full copy of the array roughly every 500ms.
    const callPutMix = calculateCallPutMix(searchedTrades);
    const directionalPremium = calculateDirectionalFlowValue(
      searchedTrades,
      (trade) => trade.premium || 0,
    );
    return {
      putContracts: callPutMix.putContracts,
      callContracts: callPutMix.callContracts,
      putPremium: callPutMix.putPremium,
      callPremium: callPutMix.callPremium,
      bullishPremium: directionalPremium.bullish,
      bearishPremium: directionalPremium.bearish,
      neutralPremium: directionalPremium.neutral,
    };
  }, [searchedTrades]);

  const darkpoolBaseForTable = React.useMemo(() => {
    let result = filteredDarkpoolBase;

    const searchedTicker = searchValue.trim();
    if (searchedTicker) {
      result = result.filter((trade) => !!trade.ticker && trade.ticker.includes(searchedTicker));
    }

    if (isFlowWatchListOnly) {
      result = result.filter((trade) => !!trade.ticker && bookmarkedItems.has(trade.ticker));
    }

    return result;
  }, [filteredDarkpoolBase, searchValue, isFlowWatchListOnly, bookmarkedItems]);

  const filterDarkpoolTradesByFeedType = React.useCallback(
    (feedType: FeedType) => {
      if (feedType === FeedType.Dark) {
        return darkpoolBaseForTable.filter((t) => t.exchangeId === FINRA_EXCHANGE_ID);
      } else if (feedType === FeedType.Lit) {
        return darkpoolBaseForTable.filter((t) => t.exchangeId !== FINRA_EXCHANGE_ID);
      }
      return darkpoolBaseForTable;
    },
    [darkpoolBaseForTable],
  );

  const filteredDarkpoolTradesPrimary = React.useMemo(
    () => filterDarkpoolTradesByFeedType(visibleFeedTypes[TABLE_IDS.PRIMARY]),
    [filterDarkpoolTradesByFeedType, visibleFeedTypes],
  );

  const filteredDarkpoolTradesSecondary = React.useMemo(
    () => filterDarkpoolTradesByFeedType(visibleFeedTypes[TABLE_IDS.SECONDARY]),
    [filterDarkpoolTradesByFeedType, visibleFeedTypes],
  );

  // Create button groups for primary and secondary tables
  const buttonGroupPrimary = (
    <FeedTypeButtonGroup
      feedType={visibleFeedTypes[TABLE_IDS.PRIMARY]}
      setFeedType={(ft) => updateFeedType(TABLE_IDS.PRIMARY, ft)}
    />
  );
  const buttonGroupSecondary = (
    <FeedTypeButtonGroup
      feedType={visibleFeedTypes[TABLE_IDS.SECONDARY]}
      setFeedType={(ft) => updateFeedType(TABLE_IDS.SECONDARY, ft)}
    />
  );

  const [archiveFlowConfigMutation] = useArchiveFlowConfigurationByPkMutation();

  const handleLoadFilters = () => {
    setIsListFilterModalOpen(true);
  };

  const { data: flowfilterData, refetch: refetchFlow } = useGetUserFlowConfigurationsQuery({
    variables: {
      userId: viewer?.viewer?.uid ?? '',
    },
    fetchPolicy: 'network-only',
    skip: !viewer?.viewer?.uid,
  });
  const [getflowFiltersForUser, { data: filtersDataForUsers, refetch: refetchFilters }] =
    useGetUserFlowConfigurationsLazyQuery({
      fetchPolicy: 'network-only',
    });

  const handleDeleteFilter = async (id: string) => {
    try {
      await archiveFlowConfigMutation({
        variables: {
          id,
          archivedAt: new Date().toISOString(),
        },
      });
      if (refetchFilters) {
        refetchFilters();
      }
    } catch (error) {
      console.error('Archiving failed:', error);
    }
  };

  const handleChangeFilter = (title: string, index: number | string) => {
    setActiveFilter(title);
    let presetObject;

    if (typeof index === 'number' && index >= 0) {
      presetObject = presetObjects[index as PresetType];
    } else if (typeof index === 'string') {
      const savedFilter = flowfilterData?.flow_configurations?.find((item) => item.id === index);
      presetObject = savedFilter?.configuration;
    }

    if (!presetObject) {
      console.warn(`No filter configuration found for "${title}" (index: ${index})`);
      return;
    }

    if (Object.keys(presetObject).length === 0) {
      dispatch({ type: ActionTypes.Reset });

      dispatch({
        type: ActionTypes.Set,
        payload: { items: normalizeFlowFilterState(presetObject) },
      });

      return;
    }

    const newState =
      typeof index === 'number'
        ? mergeFlowPresetState(filterState, presetObject)
        : normalizeFlowFilterState(presetObject);

    dispatch({
      type: ActionTypes.Set,
      payload: { items: newState },
    });
  };

  const handleSoundToggle = (enabled: boolean) => {
    setIsFlowSoundEnabled(enabled);

    if (viewer?.viewer?.uid) {
      updateUserSoundSetting({
        variables: {
          id: viewer.viewer.uid,
          isSoundEnabled: enabled,
        },
      }).catch((error) => {
        console.error('Failed to update sound setting:', error);
        setIsFlowSoundEnabled(!enabled);
      });
    }
  };

  React.useEffect(() => {
    // @ts-ignore doing this for puppeteer
    window.RENDERED_DATA = { tradesByTicker };
  }, [tradesByTicker]);

  React.useEffect(() => {
    if (isFlowSoundEnabled && displayedTrades.length > 0) {
      try {
        audio?.play();
      } catch (_error) {
        // NOTE: Ignore error. Throws if user hasn't interacted with doc
      }
    }
  }, [audio, isFlowSoundEnabled, displayedTrades.length]);

  React.useEffect(() => {
    if (currentUser?.isSoundEnabled !== undefined) {
      setIsFlowSoundEnabled(currentUser.isSoundEnabled);
    }
  }, [currentUser?.isSoundEnabled]);

  const renderFlowWidget = (key: RightColumnList): React.ReactNode => {
    switch (key) {
      case RightColumnList.TickerGraphs:
        return (
          <TopTickerGraphs
            presetGraph={topTickerGraph}
            tradesByTicker={tradesByTicker}
            metricMode={flowMetricMode}
          />
        );
      case RightColumnList.SectorGraphs:
        return (
          <SectorGraphs
            tradesBySector={tradesBySector}
            isUnusualFiltered={filterState.tradeType.includes('Unusual')}
            isSweepsFiltered={filterState.tradeType.includes('Sweeps')}
            activeSectorGraph={activeSectorGraph}
            setActiveSectorGraph={setActiveSectorGraph}
            metricMode={flowMetricMode}
          />
        );
      case RightColumnList.DarkPool:
        return (
          <DarkPoolTable
            isWatchListOnly={isFlowWatchListOnly}
            setIsWatchListOnly={setIsFlowWatchListOnly}
            hasWatchListItems={hasWatchListItems}
            watchListTickers={watchListTickers}
            viewer={viewer}
            searchValue={searchValue}
            hasAccess={hasAccess || !!injectedPageData}
            trades={filteredDarkpoolTradesPrimary}
            loading={loading}
            error={error}
            setIsWatchListModalOpen={setIsWatchListModalOpen}
            handleBookmark={handleBookmark}
            earningsMap={earningsMaps}
            tickerDetailsMap={tickerDetailsMap}
            hideEarningsToggle={false}
            right={buttonGroupPrimary}
            title=""
            includeDate={shouldShowDatePicker}
          />
        );
      case RightColumnList.DarkPoolCharts:
        return (
          <DarkPoolGraphs
            presetGraph={topTickerGraph}
            tradesByTicker={filteredDarkPoolByTicker}
            tradesBySector={darkPoolBySector}
          />
        );
      case RightColumnList.Signals:
        return (
          <SignalAlertTable
            openInNewTab
            watchListTickers={watchListTickers}
            viewer={viewer}
            searchValue={searchValue}
          />
        );
      case RightColumnList.WatchList:
        return (
          <WatchList
            isFlowWatchListOnly={isFlowWatchListOnly}
            setIsFlowWatchListOnly={setIsFlowWatchListOnly}
            isAggregateWatchListOnly={isAggregateWatchListOnly}
            setIsAggregateWatchListOnly={setIsAggregateWatchListOnly}
          />
        );
      case RightColumnList.TopNews:
        return <TopNewsEvents viewer={viewer} />;
      case RightColumnList.TopMovers:
        return <TopMoversTable watchListTickers={watchListTickers} />;
      case RightColumnList.MostResearched:
        return <MostActiveTickers watchListTickers={watchListTickers} viewer={viewer} />;
      default:
        return null;
    }
  };

  const renderFlowWidgetMenu = (
    activeKey: RightColumnList,
    onSelect: (key: RightColumnList) => void,
  ) => (
    <Menu as="div" className="relative inline-block text-left">
      <div className="w-56">
        <Menu.Button className="flex w-full items-center justify-between rounded-md border border-brand-primary bg-palette-black-1 px-4 py-2 text-xs font-medium text-white shadow-sm hover:bg-brand-primary">
          {rightColumnItems.find((item) => item.key === activeKey)?.name}
          <CaretDown16 />
        </Menu.Button>
      </div>
      <Transition
        as={React.Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 mt-1 w-56 origin-top-right rounded-md border border-palette-border bg-palette-black-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          {rightColumnItems.map((item) => (
            <Menu.Item key={item.key}>
              {({ active }) => (
                <button
                  onClick={() => onSelect(item.key)}
                  className={classNames(
                    active ? 'bg-brand-primary text-white' : 'text-gray-200',
                    'flex w-full items-center px-4 py-3 text-xs',
                  )}
                >
                  {item.name}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );

  return (
    <>
      {!!injectedPageData && <div id="hasData" style={{ display: 'none' }} />}
      <div className="h-full w-full">
        <PageTitle
          Icon={Icon}
          title={title}
          left={
            <>
              <WelcomeSlidePanel />
              <MarketDataStatus className="ml-1.5 mr-1" surface="flow" flowAccess={flowAccess} />
              {isRealtimeFlow && (
                <FlowAiAnalysisModal
                  isPageLoading={loading}
                  putContracts={putContracts}
                  callContracts={callContracts}
                  putPremium={putPremium}
                  callPremium={callPremium}
                  flowSideLabel={flowSideLabel}
                  bullishPremium={bullishPremium}
                  bearishPremium={bearishPremium}
                  neutralPremium={neutralPremium}
                />
              )}
            </>
          }
          right={
            <div className="ml-4 flex min-w-0 flex-1 items-center justify-end">
              <div className="mr-2 hidden flex-shrink-0 items-center gap-2 flowbar:flex">
                <FiltersButton
                  filterCount={numberOfFilters}
                  onClick={() => setIsCommandCenterOpen(true)}
                  compact
                />
                <div className="h-5 w-px flex-shrink-0 bg-white/10" />
                {/* Mode is the least filter-y control, so it only joins the inline bar on
                    the widest screens (2xl); below that it lives in the Filters modal,
                    which keeps the bar narrow enough to clear the header on ~14" laptops. */}
                <div className="hidden 2xl:flex">
                  <FlowSegmentedControl
                    ariaLabel="Flow metric mode"
                    tooltip="Metric · Total sums all premium, Net subtracts bearish from bullish"
                    items={FLOW_METRIC_MODE_ITEMS}
                    isActive={(value) => flowMetricMode === value}
                    onSelect={(value) => handleFlowMetricModeChange(value as FlowMetricMode)}
                  />
                </div>
                <FlowSidesSelector
                  activeSides={filterState.flowSideBasis}
                  onChange={handleFlowSidesChange}
                  arePremiumSidesLocked={arePremiumSidesLocked}
                />
                <FlowTickerTypeSelector
                  activeType={filterState.tt}
                  onChange={handleTickerTypeChange}
                />
                <FlowPresetSelector
                  activePreset={PRESET_TAB_TYPES.includes(presetIndex) ? presetIndex : null}
                  onSelect={handleSelectPreset}
                  onClear={handleClearPreset}
                />
              </div>
              <form
                className="hidden sm:block"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!searchValue.trim()) {
                    return;
                  }
                  window.open(`${TICKER_FLOW_PAGE}/${searchValue.trim()}`, '_blank');
                }}
              >
                <MiniInput
                  isSearch
                  placeholder="Search ticker (ex. TSLA)"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value.toUpperCase())}
                  reset={() => setSearchValue('')}
                />
              </form>
            </div>
          }
        />
        <LayoutGrid enableTripleColumns={!injectedPageData}>
          <MainColumnDynamic
            rows={getRowsForLayout(layoutIndex)}
            autoRows={HEADER_ROW_COUNT + additonalRows}
          >
            {shouldShowDatePicker && (
              <div>
                <Card className="w-full p-2 md:p-4">
                  <form
                    autoComplete="off"
                    className="flex flex-col gap-2 md:flex-row md:items-end md:gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      !!handleHistoricalSearch && handleHistoricalSearch(filterState.flowSideBasis);
                    }}
                  >
                    <div className="grid grid-cols-2 gap-2 md:contents">
                      <FieldWrapper className="w-full md:mr-2">
                        <Label className="text-xs">Start Date</Label>
                        <DatePicker
                          datePickerType="single"
                          value={startDate}
                          onChange={(_d, dateString) => {
                            !!setStartDate && setStartDate(dateString);
                          }}
                        >
                          <DatePickerInput
                            hideLabel
                            placeholder="mm/dd/yyyy"
                            labelText=""
                            id="start-date-calendar"
                          />
                        </DatePicker>
                      </FieldWrapper>
                      <FieldWrapper className="w-full md:mr-2">
                        <Label className="text-xs">End Date</Label>
                        <DatePicker
                          datePickerType="single"
                          value={endDate}
                          onChange={(_d, dateString) => {
                            !!setEndDate && setEndDate(dateString);
                          }}
                        >
                          <DatePickerInput
                            hideLabel
                            placeholder="mm/dd/yyyy"
                            labelText=""
                            id="end-date-calendar"
                          />
                        </DatePicker>
                      </FieldWrapper>
                    </div>
                    <FieldWrapper className="w-full md:mr-2">
                      <Label className="text-xs">Tickers</Label>
                      <div className="relative">
                        <Combobox value={null} onChange={handleTickerSelection}>
                          <Combobox.Input
                            className="h-9 w-full rounded border-2 border-palette-black-5 bg-palette-black-2 px-4 py-2 text-sm text-white outline-none focus:border-brand-primary"
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              handleTickerInputChange(event.target.value);
                            }}
                            placeholder="Ex. TSLA,AAPL,GME"
                            autoComplete="off"
                            value={tickers || ''}
                            displayValue={() => tickers || ''}
                          />
                          {tickerSearchResults?.length > 0 && (
                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto border border-palette-border bg-palette-black-1 py-1 text-base shadow-lg focus:outline-none sm:text-sm">
                              {tickerSearchResults.map((ticker) => (
                                <Combobox.Option
                                  key={ticker.t}
                                  value={ticker}
                                  className={({ active }) =>
                                    classNames(
                                      'relative mb-0 cursor-default select-none px-2 py-1',
                                      active ? 'bg-brand-primary text-white' : 'text-white',
                                    )
                                  }
                                >
                                  {({ selected }) => (
                                    <>
                                      <span
                                        className={classNames(
                                          'block truncate',
                                          selected && 'font-semibold',
                                        )}
                                      >
                                        {ticker.n}
                                      </span>
                                    </>
                                  )}
                                </Combobox.Option>
                              ))}
                            </Combobox.Options>
                          )}
                        </Combobox>
                      </div>
                    </FieldWrapper>
                    <FieldWrapper className="hidden md:mr-2 md:block md:w-2/3">
                      <div className="flex h-10 items-center px-3">
                        <span className="text-sm font-medium text-gray-500">Max 10K Results</span>
                      </div>
                    </FieldWrapper>
                    <Button type="submit" className="w-full md:w-auto">
                      Search
                    </Button>
                  </form>
                </Card>
              </div>
            )}
            <CardRow>
              <Card className="w-full md:w-1/3">
                <div className="flex items-center">
                  <Dropdown
                    containerStyles={{ width: '100%' }}
                    wrapperStyles={{ width: '100%' }}
                    triggerStyles={{ width: '100%', minWidth: 'auto' }}
                    triggerText={
                      PAGE_DROPDOWN_ITEMS.find((item) => item.href === pageHref)?.name ||
                      'Choose Active Flow'
                    }
                    items={PAGE_DROPDOWN_ITEMS}
                  />
                  <CommandCenter
                    isCommandCenterOpen={isCommandCenterOpen}
                    setIsCommandCenterOpen={setIsCommandCenterOpen}
                    currentFilters={filterState}
                    currentFilterCount={numberOfFilters}
                    setCurrentFilters={applyCurrentFilters}
                    resetCurrentFilters={resetCurrentFilters}
                    allOptionFlowTrades={trades}
                    allDarkPoolTrades={darkpoolTrades}
                    watchListTickers={watchListTickers}
                    tradesByTickerMap={tradesByTickerMap}
                    tabsitems={PRESET_TABS}
                    tabPresetTypes={PRESET_TAB_TYPES}
                    selectedTab={PRESET_TAB_TYPES.indexOf(presetIndex)}
                    handleLoadFilters={handleLoadFilters}
                    handleDeleteFilter={handleDeleteFilter}
                    filtersDataForUsers={filtersDataForUsers}
                    getflowFiltersForUsers={getflowFiltersForUser}
                    refetchFilters={refetchFilters}
                    handleChangeFilter={handleChangeFilter}
                    activeFilter={activeFilter}
                    setActiveFilter={setActiveFilter}
                    previewTrades={previewOptionFlowTrades}
                    isPreviewLoading={props.isPreviewLoading}
                    onFlowSidePreviewRequest={props.onFlowSidePreviewRequest}
                    arePremiumSidesLocked={arePremiumSidesLocked}
                    showBaselineToggle={props.showBaselineToggle}
                    canUseBaseline={props.canUseBaseline}
                    showBaseline={props.showBaseline}
                    onToggleBaseline={props.onToggleBaseline}
                  />
                </div>
                {flowSliderReplacement ? (
                  flowSliderReplacement
                ) : (
                  <>
                    <div className="mt-2 hidden lg:block">
                      <Slider
                        items={SLIDER_TABS}
                        activeTab={layoutIndex}
                        setActiveTab={setLayoutIndex}
                      />
                    </div>
                    <div className="mt-2 block lg:hidden">
                      <div className="flex justify-center">
                        <div className="relative w-full">
                          <select
                            value={mobileLayoutIndex}
                            onChange={(e) => {
                              e.preventDefault();

                              if (
                                e.target.value === '0' ||
                                e.target.value === '1' ||
                                e.target.value === '2'
                              ) {
                                setMobileLayoutIndex(parseInt(e.target.value, 10));
                              } else {
                                setMobileLayoutIndex(e.target.value);
                              }
                            }}
                            className="form-select m-0
                                  block
                                  w-full
                                  appearance-none
                                  rounded
                                  border
                                  border-solid
                                  border-button-color
                                  bg-palette-black-2 bg-clip-padding bg-no-repeat
                                  px-3 py-1.5 text-sm
                                  font-normal
                                  text-palette-black-9
                                  transition
                                  ease-in-out
                                  focus:border-blue-600 focus:bg-palette-black-2 focus:outline-none"
                            aria-label="Default select example"
                          >
                            <option value={LayoutType.Stacked}>Stacked</option>
                            <option value={LayoutType.Market}>Market</option>
                            <option value={LayoutType.Flow}>Option Flow</option>
                            {rightColumnItems.map((item) => (
                              <option key={item.key} value={item.key}>
                                {item.text}
                              </option>
                            ))}
                          </select>
                          <div className="absolute right-3 top-2 pt-0.5">
                            <CaretDown16 />
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </Card>
              <PutCallCard
                className="ml-2 hidden md:flex"
                bullishPremium={bullishPremium}
                bearishPremium={bearishPremium}
                neutralPremium={neutralPremium}
              />
              <CallPutMixCard
                className="ml-2 hidden md:flex"
                callContracts={callContracts}
                putContracts={putContracts}
                callPremium={callPremium}
                putPremium={putPremium}
              />
            </CardRow>
            {!isMobile &&
              (layoutIndex === LayoutType.Market || layoutIndex === LayoutType.Stacked) && (
                <div>
                  <OptionAggregatedTable
                    isWeekly={isWeekly}
                    tradesByTicker={tradesByTicker}
                    darkPoolByTicker={filteredDarkPoolByTicker}
                    aggregateSortKey={aggregateSortKey}
                    setAggregateSortKey={setAggregateSortKey}
                    aggregateSortDirection={aggregateSortDirection}
                    setAggregateSortDirection={setAggregateSortDirection}
                    isWatchListOnly={isAggregateWatchListOnly}
                    setIsWatchListOnly={setIsAggregateWatchListOnly}
                    hasWatchListItems={hasWatchListItems}
                    watchListTickers={watchListTickers}
                    hasAccess={hasAccess || !!injectedPageData}
                    setIsWatchListModalOpen={setIsWatchListModalOpen}
                    handleBookmark={handleBookmark}
                    earningsMap={earningsMaps}
                    pageHref={pageHref}
                    metricMode={flowMetricMode}
                    delayedFlowWait={delayedFlowWait}
                  />
                </div>
              )}
            {!isMobile &&
              (layoutIndex === LayoutType.Flow || layoutIndex === LayoutType.Stacked) && (
                <div className="h-full min-h-0 overflow-hidden">
                  <OptionFlowTable
                    totalFlowCount={totalFlowCount}
                    trades={displayedTrades}
                    referenceTrades={trades}
                    flowSortKey={flowSortKey}
                    setFlowSortKey={setFlowSortKey}
                    flowSortDirection={flowSortDirection}
                    setFlowSortDirection={setFlowSortDirection}
                    isWatchListOnly={isFlowWatchListOnly}
                    setIsWatchListOnly={setIsFlowWatchListOnly}
                    hasWatchListItems={hasWatchListItems}
                    isSoundEnabled={isFlowSoundEnabled}
                    setIsSoundEnabled={handleSoundToggle}
                    watchListTickers={watchListTickers}
                    hasAccess={hasAccess || !!injectedPageData}
                    isUserSession={isUserSession}
                    setIsWatchListModalOpen={setIsWatchListModalOpen}
                    handleBookmark={handleBookmark}
                    earningsMap={earningsMaps}
                    tradesByTicker={tradesByTicker}
                    flowSideBasis={filterState.flowSideBasis}
                    flowSideLabel={flowSideLabel}
                    putFlowBasis={filterState.putFlowBasis}
                    metricMode={flowMetricMode}
                    delayedFlowWait={delayedFlowWait}
                    onOpenPopover={handleOpenPopover}
                    isMobile={false}
                  />
                </div>
              )}
            <div className="grid h-full grid-rows-2 gap-2 lg:hidden">
              {mobileLayoutIndex === LayoutType.Stacked && (
                <>
                  <div className="overflow-hidden">
                    <OptionAggregatedTable
                      isWeekly={isWeekly}
                      tradesByTicker={tradesByTicker}
                      darkPoolByTicker={filteredDarkPoolByTicker}
                      aggregateSortKey={aggregateSortKey}
                      setAggregateSortKey={setAggregateSortKey}
                      aggregateSortDirection={aggregateSortDirection}
                      setAggregateSortDirection={setAggregateSortDirection}
                      isWatchListOnly={isAggregateWatchListOnly}
                      setIsWatchListOnly={setIsAggregateWatchListOnly}
                      hasWatchListItems={hasWatchListItems}
                      watchListTickers={watchListTickers}
                      hasAccess={hasAccess || !!injectedPageData}
                      setIsWatchListModalOpen={setIsWatchListModalOpen}
                      handleBookmark={handleBookmark}
                      earningsMap={earningsMaps}
                      pageHref={pageHref}
                      metricMode={flowMetricMode}
                      delayedFlowWait={delayedFlowWait}
                    />
                  </div>
                  <div className="overflow-hidden">
                    <OptionFlowTable
                      totalFlowCount={totalFlowCount}
                      trades={displayedTrades}
                      referenceTrades={trades}
                      flowSortKey={flowSortKey}
                      setFlowSortKey={setFlowSortKey}
                      flowSortDirection={flowSortDirection}
                      setFlowSortDirection={setFlowSortDirection}
                      isWatchListOnly={isFlowWatchListOnly}
                      setIsWatchListOnly={setIsFlowWatchListOnly}
                      hasWatchListItems={hasWatchListItems}
                      isSoundEnabled={isFlowSoundEnabled}
                      setIsSoundEnabled={handleSoundToggle}
                      watchListTickers={watchListTickers}
                      hasAccess={hasAccess || !!injectedPageData}
                      isUserSession={isUserSession}
                      setIsWatchListModalOpen={setIsWatchListModalOpen}
                      handleBookmark={handleBookmark}
                      earningsMap={earningsMaps}
                      flowSideBasis={filterState.flowSideBasis}
                      flowSideLabel={flowSideLabel}
                      putFlowBasis={filterState.putFlowBasis}
                      metricMode={flowMetricMode}
                      delayedFlowWait={delayedFlowWait}
                      onOpenPopover={handleOpenPopover}
                      isMobile={true}
                    />
                  </div>
                </>
              )}
              {mobileLayoutIndex === LayoutType.Market && (
                <div className="row-span-2 overflow-hidden">
                  <OptionAggregatedTable
                    isWeekly={isWeekly}
                    tradesByTicker={tradesByTicker}
                    darkPoolByTicker={filteredDarkPoolByTicker}
                    aggregateSortKey={aggregateSortKey}
                    setAggregateSortKey={setAggregateSortKey}
                    aggregateSortDirection={aggregateSortDirection}
                    setAggregateSortDirection={setAggregateSortDirection}
                    isWatchListOnly={isAggregateWatchListOnly}
                    setIsWatchListOnly={setIsAggregateWatchListOnly}
                    hasWatchListItems={hasWatchListItems}
                    watchListTickers={watchListTickers}
                    hasAccess={hasAccess || !!injectedPageData}
                    setIsWatchListModalOpen={setIsWatchListModalOpen}
                    handleBookmark={handleBookmark}
                    earningsMap={earningsMaps}
                    pageHref={pageHref}
                    metricMode={flowMetricMode}
                    delayedFlowWait={delayedFlowWait}
                  />
                </div>
              )}
              {mobileLayoutIndex === LayoutType.Flow && (
                <div className="row-span-2 overflow-hidden">
                  <OptionFlowTable
                    totalFlowCount={totalFlowCount}
                    trades={displayedTrades}
                    referenceTrades={trades}
                    flowSortKey={flowSortKey}
                    setFlowSortKey={setFlowSortKey}
                    flowSortDirection={flowSortDirection}
                    setFlowSortDirection={setFlowSortDirection}
                    isWatchListOnly={isFlowWatchListOnly}
                    setIsWatchListOnly={setIsFlowWatchListOnly}
                    hasWatchListItems={hasWatchListItems}
                    isSoundEnabled={isFlowSoundEnabled}
                    setIsSoundEnabled={handleSoundToggle}
                    watchListTickers={watchListTickers}
                    hasAccess={hasAccess || !!injectedPageData}
                    isUserSession={isUserSession}
                    setIsWatchListModalOpen={setIsWatchListModalOpen}
                    handleBookmark={handleBookmark}
                    earningsMap={earningsMaps}
                    flowSideBasis={filterState.flowSideBasis}
                    flowSideLabel={flowSideLabel}
                    putFlowBasis={filterState.putFlowBasis}
                    metricMode={flowMetricMode}
                    delayedFlowWait={delayedFlowWait}
                    onOpenPopover={handleOpenPopover}
                    isMobile={true}
                  />
                </div>
              )}
              {mobileLayoutIndex === RightColumnList.TickerGraphs && (
                <div className="row-span-2 overflow-hidden">
                  <TopTickerGraphs
                    presetGraph={topTickerGraph}
                    tradesByTicker={tradesByTicker}
                    metricMode={flowMetricMode}
                  />
                </div>
              )}
              {mobileLayoutIndex === RightColumnList.SectorGraphs && (
                <div className="row-span-2 overflow-hidden">
                  <SectorGraphs
                    tradesBySector={tradesBySector}
                    isUnusualFiltered={filterState.tradeType.includes('Unusual')}
                    isSweepsFiltered={filterState.tradeType.includes('Sweeps')}
                    activeSectorGraph={activeSectorGraph}
                    setActiveSectorGraph={setActiveSectorGraph}
                    metricMode={flowMetricMode}
                  />
                </div>
              )}
              {mobileLayoutIndex === RightColumnList.DarkPool && (
                <div className="row-span-2 overflow-hidden">
                  <DarkPoolTable
                    isWatchListOnly={isFlowWatchListOnly}
                    setIsWatchListOnly={setIsFlowWatchListOnly}
                    hasWatchListItems={hasWatchListItems}
                    watchListTickers={watchListTickers}
                    viewer={viewer}
                    searchValue={searchValue}
                    hasAccess={hasAccess || !!injectedPageData}
                    trades={filteredDarkpoolTradesPrimary}
                    loading={loading}
                    error={error}
                    setIsWatchListModalOpen={setIsWatchListModalOpen}
                    handleBookmark={handleBookmark}
                    earningsMap={earningsMaps}
                    tickerDetailsMap={tickerDetailsMap}
                    hideEarningsToggle={false}
                    right={buttonGroupPrimary}
                    title=""
                    includeDate={shouldShowDatePicker}
                  />
                </div>
              )}
              {mobileLayoutIndex === RightColumnList.DarkPoolCharts && (
                <div className="row-span-2 overflow-hidden">
                  <DarkPoolGraphs
                    presetGraph={topTickerGraph}
                    tradesByTicker={filteredDarkPoolByTicker}
                    tradesBySector={darkPoolBySector}
                  />
                </div>
              )}
              {mobileLayoutIndex === RightColumnList.Signals && (
                <div className="row-span-2 overflow-hidden">
                  <SignalAlertTable
                    openInNewTab
                    watchListTickers={watchListTickers}
                    viewer={viewer}
                    searchValue={searchValue}
                  />
                </div>
              )}
              {mobileLayoutIndex === RightColumnList.WatchList && (
                <div className="row-span-2 overflow-hidden">
                  <WatchList
                    isFlowWatchListOnly={isFlowWatchListOnly}
                    setIsFlowWatchListOnly={setIsFlowWatchListOnly}
                    isAggregateWatchListOnly={isAggregateWatchListOnly}
                    setIsAggregateWatchListOnly={setIsAggregateWatchListOnly}
                  />
                </div>
              )}
              {mobileLayoutIndex === RightColumnList.TopNews && (
                <div className="row-span-2 overflow-hidden">
                  <TopNewsEvents viewer={viewer} />
                </div>
              )}
              {mobileLayoutIndex === RightColumnList.TopMovers && (
                <div className="row-span-2 overflow-hidden">
                  <TopMoversTable watchListTickers={watchListTickers} />
                </div>
              )}
              {mobileLayoutIndex === RightColumnList.MostResearched && (
                <div className="row-span-2 overflow-hidden">
                  <MostActiveTickers watchListTickers={watchListTickers} viewer={viewer} />
                </div>
              )}
            </div>
          </MainColumnDynamic>
          {/* NOTE: Hiding this since they don't get a choice at large screen and either shows graphs or a 4x4 grid */}
          <HideLarge className="h-full">
            <StackedColumn enableTripleColumns={!injectedPageData} numRows={2}>
              <div className={getSinglePaneClass('top', splitLayout.expandedPane)}>
                <LayoutButtons className="mb-2 flex items-center justify-end gap-2">
                  {isFlowExpandable(activeRightColumnSectionTop) && (
                    <LayoutToggleButton
                      isExpanded={splitLayout.isExpanded('top')}
                      onClick={() => handleToggleExpandPane('top', activeRightColumnSectionTop)}
                    />
                  )}
                  <Menu as="div" className="relative inline-block text-left">
                    <div className="w-56">
                      <Menu.Button className="flex w-full items-center justify-between rounded-md border border-brand-primary bg-palette-black-1 px-4 py-2 text-xs font-medium text-white shadow-sm hover:bg-brand-primary">
                        {
                          rightColumnItems?.find((item) => item.key === activeRightColumnSectionTop)
                            ?.name
                        }
                        <CaretDown16 />
                      </Menu.Button>
                    </div>

                    <Transition
                      as={React.Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-1 w-56 origin-top-right rounded-md border border-palette-border bg-palette-black-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        {rightColumnItems.map((item) => (
                          <Menu.Item key={item.key}>
                            {({ active }) => (
                              <button
                                onClick={() => handleSelectTopWidget(item.key)}
                                className={classNames(
                                  active ? 'bg-brand-primary text-white' : 'text-gray-200',
                                  'flex w-full items-center px-4 py-3 text-xs',
                                )}
                              >
                                {item.name}
                              </button>
                            )}
                          </Menu.Item>
                        ))}
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </LayoutButtons>
                {/* <Card className="flex flex-col w-full h-full" isDark>
                  <DropdownToggleWrapper className="flex items-center justify-between">
                    <div className="text-sm font-semibold">Change Layout</div>
                    <div className="w-1/2 ml-1">
                      <Dropdown
                        isButtonPrimary
                        triggerStyles={{ width: '100%' }}
                        wrapperStyles={{ width: '100%' }}
                        triggerText={
                          rightColumnItems.find((item) => item.key === activeRightColumnSection)
                            ?.name || ''
                        }
                        items={rightColumnItems.map((item) => ({
                          name: item.name,
                          onClick: () => setActiveRightColumnSection(item.key),
                        }))}
                      />
                    </div>
                  </DropdownToggleWrapper>
                </Card> */}
                <LayoutButtonRow>
                  {activeRightColumnSectionTop === RightColumnList.TickerGraphs && (
                    <TopTickerGraphs
                      presetGraph={topTickerGraph}
                      tradesByTicker={tradesByTicker}
                      metricMode={flowMetricMode}
                    />
                  )}
                  {activeRightColumnSectionTop === RightColumnList.SectorGraphs && (
                    <SectorGraphs
                      tradesBySector={tradesBySector}
                      isUnusualFiltered={filterState.tradeType.includes('Unusual')}
                      isSweepsFiltered={filterState.tradeType.includes('Sweeps')}
                      activeSectorGraph={activeSectorGraph}
                      setActiveSectorGraph={setActiveSectorGraph}
                      metricMode={flowMetricMode}
                    />
                  )}
                  {activeRightColumnSectionTop === RightColumnList.DarkPool && (
                    <DarkPoolTable
                      isWatchListOnly={isFlowWatchListOnly}
                      setIsWatchListOnly={setIsFlowWatchListOnly}
                      hasWatchListItems={hasWatchListItems}
                      watchListTickers={watchListTickers}
                      viewer={viewer}
                      searchValue={searchValue}
                      hasAccess={hasAccess || !!injectedPageData}
                      trades={filteredDarkpoolTradesPrimary}
                      loading={loading}
                      error={error}
                      setIsWatchListModalOpen={setIsWatchListModalOpen}
                      handleBookmark={handleBookmark}
                      earningsMap={earningsMaps}
                      tickerDetailsMap={tickerDetailsMap}
                      hideEarningsToggle={false}
                      right={buttonGroupPrimary}
                      title=""
                      includeDate={shouldShowDatePicker}
                    />
                  )}
                  {activeRightColumnSectionTop === RightColumnList.DarkPoolCharts && (
                    <DarkPoolGraphs
                      presetGraph={topTickerGraph}
                      tradesByTicker={filteredDarkPoolByTicker}
                      tradesBySector={darkPoolBySector}
                    />
                  )}
                  {activeRightColumnSectionTop === RightColumnList.Signals && (
                    <SignalAlertTable
                      openInNewTab
                      watchListTickers={watchListTickers}
                      viewer={viewer}
                      searchValue={searchValue}
                    />
                  )}
                  {activeRightColumnSectionTop === RightColumnList.WatchList && (
                    <WatchList
                      isFlowWatchListOnly={isFlowWatchListOnly}
                      setIsFlowWatchListOnly={setIsFlowWatchListOnly}
                      isAggregateWatchListOnly={isAggregateWatchListOnly}
                      setIsAggregateWatchListOnly={setIsAggregateWatchListOnly}
                    />
                  )}
                  {activeRightColumnSectionTop === RightColumnList.TopNews && (
                    <TopNewsEvents viewer={viewer} />
                  )}
                  {activeRightColumnSectionTop === RightColumnList.TopMovers && (
                    <TopMoversTable watchListTickers={watchListTickers} />
                  )}
                  {activeRightColumnSectionTop === RightColumnList.MostResearched && (
                    <MostActiveTickers watchListTickers={watchListTickers} viewer={viewer} />
                  )}
                </LayoutButtonRow>
              </div>
              <div className={getSinglePaneClass('bottom', splitLayout.expandedPane)}>
                <LayoutButtons className="mb-2 flex items-center justify-end gap-2">
                  {isFlowExpandable(activeRightColumnSectionBot) && (
                    <LayoutToggleButton
                      isExpanded={splitLayout.isExpanded('bottom')}
                      onClick={() => handleToggleExpandPane('bottom', activeRightColumnSectionBot)}
                    />
                  )}
                  <Menu as="div" className="relative inline-block text-left">
                    <div className="w-56">
                      <Menu.Button className="flex w-full items-center justify-between rounded-md border border-brand-primary bg-palette-black-1 px-4 py-2 text-xs font-medium text-white shadow-sm hover:bg-brand-primary">
                        {
                          rightColumnItems?.find((item) => item.key === activeRightColumnSectionBot)
                            ?.name
                        }
                        <CaretDown16 />
                      </Menu.Button>
                    </div>

                    <Transition
                      as={React.Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-1 h-64 w-56 origin-top-right overflow-auto rounded-md border border-palette-border bg-palette-black-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        {rightColumnItems.map((item) => (
                          <Menu.Item key={item.key}>
                            {({ active }) => (
                              <button
                                onClick={() => handleSelectBottomWidget(item.key)}
                                className={classNames(
                                  active ? 'bg-brand-primary text-white' : 'text-gray-200',
                                  'flex w-full items-center px-4 py-3 text-xs',
                                )}
                              >
                                {item.name}
                              </button>
                            )}
                          </Menu.Item>
                        ))}
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </LayoutButtons>
                <LayoutButtonRow>
                  {activeRightColumnSectionBot === RightColumnList.TickerGraphs && (
                    <TopTickerGraphs
                      presetGraph={topTickerGraph}
                      tradesByTicker={tradesByTicker}
                      metricMode={flowMetricMode}
                    />
                  )}
                  {activeRightColumnSectionBot === RightColumnList.SectorGraphs && (
                    <SectorGraphs
                      tradesBySector={tradesBySector}
                      isUnusualFiltered={filterState.tradeType.includes('Unusual')}
                      isSweepsFiltered={filterState.tradeType.includes('Sweeps')}
                      activeSectorGraph={activeSectorGraph}
                      setActiveSectorGraph={setActiveSectorGraph}
                      metricMode={flowMetricMode}
                    />
                  )}
                  {activeRightColumnSectionBot === RightColumnList.DarkPool && (
                    <DarkPoolTable
                      isWatchListOnly={isFlowWatchListOnly}
                      setIsWatchListOnly={setIsFlowWatchListOnly}
                      hasWatchListItems={hasWatchListItems}
                      watchListTickers={watchListTickers}
                      viewer={viewer}
                      searchValue={searchValue}
                      hasAccess={hasAccess || !!injectedPageData}
                      trades={filteredDarkpoolTradesSecondary}
                      loading={loading}
                      error={error}
                      setIsWatchListModalOpen={setIsWatchListModalOpen}
                      handleBookmark={handleBookmark}
                      earningsMap={earningsMaps}
                      tickerDetailsMap={tickerDetailsMap}
                      hideEarningsToggle={false}
                      right={buttonGroupSecondary}
                      title=""
                      includeDate={shouldShowDatePicker}
                    />
                  )}
                  {activeRightColumnSectionBot === RightColumnList.DarkPoolCharts && (
                    <DarkPoolGraphs
                      presetGraph={topTickerGraph}
                      tradesByTicker={filteredDarkPoolByTicker}
                      tradesBySector={darkPoolBySector}
                    />
                  )}
                  {activeRightColumnSectionBot === RightColumnList.Signals && (
                    <SignalAlertTable
                      openInNewTab
                      watchListTickers={watchListTickers}
                      viewer={viewer}
                      searchValue={searchValue}
                    />
                  )}
                  {activeRightColumnSectionBot === RightColumnList.WatchList && (
                    <WatchList
                      isFlowWatchListOnly={isFlowWatchListOnly}
                      setIsFlowWatchListOnly={setIsFlowWatchListOnly}
                      isAggregateWatchListOnly={isAggregateWatchListOnly}
                      setIsAggregateWatchListOnly={setIsAggregateWatchListOnly}
                    />
                  )}
                  {activeRightColumnSectionBot === RightColumnList.TopNews && (
                    <TopNewsEvents viewer={viewer} />
                  )}
                  {activeRightColumnSectionBot === RightColumnList.TopMovers && (
                    <TopMoversTable watchListTickers={watchListTickers} />
                  )}
                  {activeRightColumnSectionBot === RightColumnList.MostResearched && (
                    <MostActiveTickers watchListTickers={watchListTickers} viewer={viewer} />
                  )}
                </LayoutButtonRow>
              </div>
            </StackedColumn>
          </HideLarge>
          {!isMobile && (
            <ShowLarge>
              <StackedColumn enableTripleColumns={!injectedPageData} numRows={2}>
                <div className={getSinglePaneClass('top', splitLayoutWideLeft.expandedPane)}>
                  <LayoutButtons className="mb-2 flex items-center justify-end gap-2" showTall>
                    {isFlowExpandable(activeWideTopLeft) && (
                      <LayoutToggleButton
                        isExpanded={splitLayoutWideLeft.isExpanded('top')}
                        onClick={() => handleToggleWideLeftPane('top', activeWideTopLeft)}
                      />
                    )}
                    {renderFlowWidgetMenu(activeWideTopLeft, handleSelectWideTopLeft)}
                  </LayoutButtons>
                  <LayoutButtonRow>{renderFlowWidget(activeWideTopLeft)}</LayoutButtonRow>
                </div>
                <div className={getSinglePaneClass('bottom', splitLayoutWideLeft.expandedPane)}>
                  <LayoutButtons className="mb-2 flex items-center justify-end gap-2" showTall>
                    {isFlowExpandable(activeWideBotLeft) && (
                      <LayoutToggleButton
                        isExpanded={splitLayoutWideLeft.isExpanded('bottom')}
                        onClick={() => handleToggleWideLeftPane('bottom', activeWideBotLeft)}
                      />
                    )}
                    {renderFlowWidgetMenu(activeWideBotLeft, handleSelectWideBotLeft)}
                  </LayoutButtons>
                  <LayoutButtonRow>{renderFlowWidget(activeWideBotLeft)}</LayoutButtonRow>
                </div>
              </StackedColumn>
            </ShowLarge>
          )}
          {!isMobile && (
            <ShowTripleColumns enableTripleColumns={!injectedPageData}>
              <StackedColumn numRows={2}>
                <div className={getSinglePaneClass('top', splitLayoutWideRight.expandedPane)}>
                  <LayoutButtons className="mb-2 flex items-center justify-end gap-2" showTall>
                    {isFlowExpandable(activeRightColumnSectionLarge) && (
                      <LayoutToggleButton
                        isExpanded={splitLayoutWideRight.isExpanded('top')}
                        onClick={() =>
                          handleToggleWideRightPane('top', activeRightColumnSectionLarge)
                        }
                      />
                    )}
                    {renderFlowWidgetMenu(activeRightColumnSectionLarge, handleSelectWideTopRight)}
                  </LayoutButtons>
                  <LayoutButtonRow>
                    {renderFlowWidget(activeRightColumnSectionLarge)}
                  </LayoutButtonRow>
                </div>
                <div className={getSinglePaneClass('bottom', splitLayoutWideRight.expandedPane)}>
                  <LayoutButtons className="mb-2 flex items-center justify-end gap-2" showTall>
                    {isFlowExpandable(activeWideBotRight) && (
                      <LayoutToggleButton
                        isExpanded={splitLayoutWideRight.isExpanded('bottom')}
                        onClick={() => handleToggleWideRightPane('bottom', activeWideBotRight)}
                      />
                    )}
                    {renderFlowWidgetMenu(activeWideBotRight, handleSelectWideBotRight)}
                  </LayoutButtons>
                  <LayoutButtonRow>{renderFlowWidget(activeWideBotRight)}</LayoutButtonRow>
                </div>
              </StackedColumn>
            </ShowTripleColumns>
          )}
        </LayoutGrid>
      </div>
      {isShowFlowDescription && <FlowDescription />}
      <ListFilterModal
        tickerData={[]}
        userId={viewer?.viewer?.uid ?? ''}
        isOpen={isListFilterModalOpen}
        handleClose={() => setIsListFilterModalOpen(false)}
        handleDeleteFilter={handleDeleteFilter}
        filtersDataForUsers={filtersDataForUsers}
        getflowFiltersForUsers={getflowFiltersForUser}
        refetchFilters={refetchFilters}
        handleChangeFilter={handleChangeFilter}
        setIsCommandCenterOpen={setIsCommandCenterOpen}
      />
      <SaveFilterModal
        isOpen={isSaveModalOpen}
        handleClose={() => setIsSaveModalOpen(false)}
        userId={viewer?.viewer?.uid || ''}
        currentFilters={filterState}
        refetchFlow={refetchFlow}
      />
      <Modal
        isOpen={isWatchListModalOpen}
        classNameMaxWidth={'max-w-2xl'}
        handleClose={handleCloseWatchListModal}
      >
        <div className="flex w-full flex-col">
          <WatchList
            onClose={handleCloseWatchListModal}
            isFlowWatchListOnly={isFlowWatchListOnly}
            setIsFlowWatchListOnly={setIsFlowWatchListOnly}
            isAggregateWatchListOnly={isAggregateWatchListOnly}
            setIsAggregateWatchListOnly={setIsAggregateWatchListOnly}
          />
        </div>
      </Modal>
      {selectedPopoverTrade && (
        <OptionsPopover
          tradesByTicker={
            tradesByTicker?.filter((t: any) => t.ticker === selectedPopoverTrade.ticker) || []
          }
          darkPoolByTicker={filteredDarkPoolByTicker?.find(
            (dp) => dp.ticker === selectedPopoverTrade.ticker,
          )}
          fullTradeData={selectedPopoverTrade}
          tickerHasEarnings={!!earningsMaps?.earnings?.[selectedPopoverTrade.ticker]}
          earningsData={earningsMaps?.earnings?.[selectedPopoverTrade.ticker]}
          daysToEarnings={
            earningsMaps?.earnings?.[selectedPopoverTrade.ticker]?.d
              ? Math.ceil(
                  (new Date(earningsMaps.earnings[selectedPopoverTrade.ticker].d).getTime() -
                    new Date().getTime()) /
                    (1000 * 60 * 60 * 24),
                )
              : null
          }
          isOpen={isPopoverOpen}
          onOpenChange={setIsPopoverOpen}
          anchorElement={popoverAnchorRef.current}
        >
          <div />
        </OptionsPopover>
      )}
    </>
  );
};

export default OptionFlowPage;
